import { useState, useEffect, useRef, useCallback } from "react";

/* ------------------------------------------------------------------
   TWSTD PRODUCTIONS — sitio de estudio
   Paleta: negro casi puro + cian/magenta a juego con el logotipo
   (el mismo tratamiento "duotone glitch" del logo se repite en
   títulos, bordes de banner y el marcador de la marca).
------------------------------------------------------------------- */

const TOKENS = {
  bg: "#0a0a0c",
  panel: "#131316",
  panel2: "#1a1a1e",
  ink: "#f2f0ea",
  muted: "#9a9aa1",
  cyan: "#8be0ff",
  magenta: "#800033",
  line: "rgba(242,240,234,0.12)",
};

const DEFAULT_SERIES_PALETTE = {
  accent1: TOKENS.cyan,
  accent2: TOKENS.magenta,
  cardBg: TOKENS.panel,
  text: TOKENS.ink,
};

const STORAGE_KEY = "twstd_site_content_v2";
const LOCAL_PASS_KEY = "twstd_local_admin_pass";
const TOKEN_KEY = "twstd_admin_token";

/* ---------------- datos por defecto (fallback inicial) ---------------- */

const DEFAULT_SERIES = [
  {
    id: "virtualDiva",
    title: "DreamVista 2008",
    eyebrow: "Serie original · terror digital",
    meta: "10 episodios · 2025–2026 (terror digital)",
    tagline: "Hay cosas que es mejor olvidar",
    synopsis:
      "Un chico explora un sistema operativo olvidado que nunca fue lanzado al público con la ayuda de la risueña asistente virtual de este; Clara",
    logoUrl: "",
    banners: [
      `url(${require("./images/dv1.jpeg")}) center/cover no-repeat`,
      `url(${require("./images/dv2.jpeg")}) center/cover no-repeat`,
      `url(${require("./images/dv3.jpeg")}) center/cover no-repeat`,
      `url(${require("./images/dv4.jpeg")}) center/cover no-repeat`,
    ],
    palette: {
      accent1: "#8be0ff",
      accent2: "#800033",
      cardBg: "#131316",
      text: "#f2f0ea",
    },
    links: [],
    files: [],
    isPlaceholder: false,
  },
  {
    id: "anomaly1",
    title: "I.S.A",
    eyebrow: "Serie original · Terror Analógico",
    meta: "10 episodios · found footage",
    tagline: "Un avión cayó, algo cayó con él",
    synopsis: "Un científico y una periodista investigan una conspiración a ",
    logoUrl: "",
    banners: [
      `url(${require("./images/isaLogo.jpeg")}) center/cover no-repeat`,
      `url(${require("./images/isa1.jpeg")}) center/cover no-repeat`,
      `url(${require("./images/isa2.png")}) center/cover no-repeat`,
      `url(${require("./images/isa3.png")}) center/cover no-repeat`,
      "linear-gradient(135deg, #0a0a0c 0%, #1a1a1e 50%, #2a2a30 100%)",
      "linear-gradient(135deg, #0a0a0c 0%, #22181e 50%, #3a2430 100%)",
    ],
    palette: {
      accent1: "#8be0ff",
      accent2: "#800033",
      cardBg: "#131316",
      text: "#f2f0ea",
    },
    links: [],
    files: [],
    isPlaceholder: true,
  },
  {
    id: "victis",
    title: "VICTIS",
    eyebrow: "Serie original",
    meta: "N.º de episodios · formato",
    tagline: "Hace 7 días, todos desaparecieron; todos, menos nosotros.",
    synopsis:
      "Luego de que una fuerza misteriosa se llevara a todos los habitantes de la tierra, Evelynn, una de los pocos supervivientes, debe encontrar una manera de traerlos de vuelta.",
    logoUrl: "",
    banners: [
      `url(${require("./images/victis1.jpeg")}) center/cover no-repeat`,
      `url(${require("./images/victis2.jpeg")}) center/cover no-repeat`,
      "linear-gradient(135deg, #0a0a0c 0%, #131c22 50%, #1e3038 100%)",
      "linear-gradient(135deg, #0a0a0c 0%, #1e1418 50%, #34202a 100%)",
    ],
    palette: {
      accent1: "#8be0ff",
      accent2: "#800033",
      cardBg: "#131316",
      text: "#f2f0ea",
    },
    links: [],
    files: [],
    isPlaceholder: true,
  },
];

const DEFAULT_PROJECTS = [
  {
    id: "ash",
    title: "Ash Call40",
    type: "Canal corto · Horror multimedia",
    status: "Activo",
    engine: "YouTube · Instagram · TikTok",
    desc: "Haciendo horror en 60 segundos",
    synopsis:
      "Proyecto de micro-ficción de terror pensado para formato vertical de 60 segundos en YouTube Shorts, Instagram Reels y TikTok.",
    logoUrl: "",
    banners: [
      "linear-gradient(135deg, #0a0a0c 0%, #1a1a1e 50%, #2a2a30 100%)",
    ],
    links: [],
    files: [],
  },
  {
    id: "anomalyV1",
    title: "ANOMALÍA - An I.S.A story",
    type: "Videojuego · terror de supervivencia en primera persona",
    status: "En desarrollo",
    engine: "Unreal Engine 5",
    desc: "Un survival horror enfocado en ambientación y combate.",
    synopsis:
      "Un survival horror en primera persona ambientado en el universo de I.S.A, enfocado en exploración atmosférica, tensión constante y combate táctico.",
    logoUrl: "",
    banners: [
      `url(${require("./images/isa1.jpeg")}) center/cover no-repeat`,
      `url(${require("./images/isa2.png")}) center/cover no-repeat`,
    ],
    links: [],
    files: [],
  },
  {
    id: "wtdv",
    title: "Welcome To DreamVista",
    type: "Videojuego · terror de supervivencia en primera persona",
    status: "Pausado",
    engine: "Unreal Engine 5",
    desc:
      "Un survival horror construido sobre un mecanismo de mundo dual: dos versiones espejo del mismo nivel, y el jugador cruza entre ellas. Arquitectura híbrida C++/Blueprint.",
    synopsis:
      "Un survival horror construido sobre un mecanismo de mundo dual: dos versiones espejo del mismo nivel, y el jugador cruza entre ellas. Arquitectura híbrida C++/Blueprint desarrollada en Unreal Engine 5.",
    logoUrl: "",
    banners: [
      `url(${require("./images/dv1.jpeg")}) center/cover no-repeat`,
      `url(${require("./images/dv2.jpeg")}) center/cover no-repeat`,
    ],
    links: [],
    files: [],
  },
  {
    id: "dobleC",
    title: "Combo Cuates Podcast",
    type: "Podcast · Cine · Comedia",
    status: "Activo",
    engine: "YouTube",
    desc: "Un podcast donde se habla sobre películas y noticias de cine",
    synopsis:
      "Un espacio conversacional donde se debate sobre estrenos, clásicos, curiosidades y noticias del mundo del cine con humor.",
    logoUrl: "",
    banners: [
      "linear-gradient(135deg, #0a0a0c 0%, #131c22 50%, #1e3038 100%)",
    ],
    links: [],
    files: [],
  },
];

const DEFAULT_NEWS = [
  {
    id: "bienvenidos-twstd",
    title: "Nuevo sitio oficial de TWSTD Productions",
    eyebrow: "Anuncio · Estudio",
    date: "Octubre 2026",
    desc: "Estrenamos portal oficial con catálogo de series, videojuegos en desarrollo y novedades del estudio.",
    synopsis:
      "Bienvenidos al sitio oficial de TWSTD Productions. Aquí encontrarás actualizaciones sobre nuestras series originales de terror digital y analógico, avances de nuestros videojuegos en Unreal Engine 5 y lanzamientos exclusivos del estudio.",
    banners: [
      `url(${require("./images/dv1.jpeg")}) center/cover no-repeat`,
    ],
    links: [],
    files: [],
  },
  {
    id: "dreamvista-produccion",
    title: "DreamVista 2008 — Avance de producción",
    eyebrow: "Noticia · Serie Original",
    date: "2025–2026",
    desc: "Nuevos episodios en camino explorando los secretos detrás del sistema operativo olvidado y su asistente Clara.",
    synopsis:
      "La producción de DreamVista 2008 continúa avanzando. Muy pronto compartiremos nuevos adelantos, material detrás de cámaras y fechas de estreno de los próximos episodios.",
    banners: [
      `url(${require("./images/dv3.jpeg")}) center/cover no-repeat`,
    ],
    links: [],
    files: [],
  },
];

const DEFAULT_MERCH = [
  {
    id: "poster-sofia",
    title: "Póster — DreamVista 2008",
    price: "$––",
    tag: "Edición serie",
    seriesId: "virtualDiva",
    status: "Próximamente",
    image: "",
    url: "",
  },
  {
    id: "pin-mask",
    title: "Pin esmaltado — máscara TWSTD",
    price: "$––",
    tag: "Estudio",
    seriesId: "",
    status: "Próximamente",
    image: "",
    url: "",
  },
  {
    id: "tee-glitch",
    title: "Playera — logotipo glitch",
    price: "$––",
    tag: "Estudio",
    seriesId: "",
    status: "Próximamente",
    image: "",
    url: "",
  },
  {
    id: "sticker-pack",
    title: "Set de calcomanías",
    price: "$––",
    tag: "Estudio",
    seriesId: "",
    status: "Próximamente",
    image: "",
    url: "",
  },
];

const DEFAULT_SITE_SETTINGS = {
  brandTitle: "TWSTD",
  brandSub: "PRODUCTIONS",
  brandLogoUrl: "",
  heroEyebrow: "Casa productora independiente",
  heroTitle: "TWSTD PRODUCTIONS",
  heroLogoUrl: "",
  heroSub: "Casa productora de contenido multimedia. Producciones 100% mexicanas.",
  heroBanners: [
    `url(${require("./images/dv1.jpeg")}) center/cover no-repeat`,
    `url(${require("./images/isaLogo.jpeg")}) center/cover no-repeat`,
    `url(${require("./images/victis1.jpeg")}) center/cover no-repeat`,
    `url(${require("./images/dv2.jpeg")}) center/cover no-repeat`,
  ],
  style: {
    bg: TOKENS.bg,
    panel: TOKENS.panel,
    panel2: TOKENS.panel2,
    ink: TOKENS.ink,
    muted: TOKENS.muted,
    cyan: TOKENS.cyan,
    magenta: TOKENS.magenta,
    bgImage: "",
  },
  merchStoreLabel: "",
  merchStoreUrl: "",
  socialLinks: [],
};

function getDefaultContent() {
  return {
    settings: {
      ...DEFAULT_SITE_SETTINGS,
      heroBanners: [...DEFAULT_SITE_SETTINGS.heroBanners],
      style: { ...DEFAULT_SITE_SETTINGS.style },
      socialLinks: [...DEFAULT_SITE_SETTINGS.socialLinks],
    },
    series: DEFAULT_SERIES.map((s) => ({
      ...s,
      banners: [...(s.banners || [])],
      palette: { ...DEFAULT_SERIES_PALETTE, ...(s.palette || {}) },
      links: [...(s.links || [])],
      files: [...(s.files || [])],
    })),
    projects: DEFAULT_PROJECTS.map((p) => ({
      ...p,
      banners: [...(p.banners || [])],
      links: [...(p.links || [])],
      files: [...(p.files || [])],
    })),
    news: DEFAULT_NEWS.map((n) => ({
      ...n,
      banners: [...(n.banners || [])],
      links: [...(n.links || [])],
      files: [...(n.files || [])],
    })),
    merch: DEFAULT_MERCH.map((m) => ({ ...m })),
    customPages: [],
  };
}

function normalizeContent(raw) {
  const fallback = getDefaultContent();
  if (!raw || typeof raw !== "object") return fallback;
  const rawSettings = raw.settings || {};
  return {
    settings: {
      ...fallback.settings,
      ...rawSettings,
      heroBanners:
        Array.isArray(rawSettings.heroBanners) && rawSettings.heroBanners.length > 0
          ? rawSettings.heroBanners
          : fallback.settings.heroBanners,
      style: {
        ...fallback.settings.style,
        ...(rawSettings.style || {}),
      },
      socialLinks: Array.isArray(rawSettings.socialLinks)
        ? rawSettings.socialLinks
        : fallback.settings.socialLinks,
    },
    series: Array.isArray(raw.series)
      ? raw.series.map((s) => ({
          ...s,
          banners: Array.isArray(s.banners) ? s.banners : [],
          palette: {
            ...DEFAULT_SERIES_PALETTE,
            ...(s.palette || {}),
          },
          links: Array.isArray(s.links) ? s.links : [],
          files: Array.isArray(s.files) ? s.files : [],
        }))
      : fallback.series,
    projects: Array.isArray(raw.projects)
      ? raw.projects.map((p) => ({
          ...p,
          banners: Array.isArray(p.banners) ? p.banners : [],
          links: Array.isArray(p.links) ? p.links : [],
          files: Array.isArray(p.files) ? p.files : [],
        }))
      : fallback.projects,
    news: Array.isArray(raw.news)
      ? raw.news.map((n) => ({
          ...n,
          banners: Array.isArray(n.banners) ? n.banners : [],
          links: Array.isArray(n.links) ? n.links : [],
          files: Array.isArray(n.files) ? n.files : [],
        }))
      : fallback.news,
    merch: Array.isArray(raw.merch) ? raw.merch : fallback.merch,
    customPages: Array.isArray(raw.customPages)
      ? raw.customPages.map((cp) => ({
          ...cp,
          banners: Array.isArray(cp.banners) ? cp.banners : [],
          links: Array.isArray(cp.links) ? cp.links : [],
          files: Array.isArray(cp.files) ? cp.files : [],
        }))
      : [],
  };
}

function formatBannerBg(bg) {
  if (!bg || typeof bg !== "string") {
    return "linear-gradient(135deg, #0a0a0c 0%, #1a1a1e 50%, #2a2a30 100%)";
  }
  const trimmed = bg.trim();
  if (
    trimmed.startsWith("url(") ||
    trimmed.startsWith("linear-gradient(") ||
    trimmed.startsWith("radial-gradient(") ||
    trimmed.startsWith("#") ||
    trimmed.startsWith("rgb")
  ) {
    return trimmed;
  }
  return `url("${trimmed}") center/cover no-repeat`;
}

function extractRawImageUrl(val) {
  if (!val || typeof val !== "string") return "";
  const trimmed = val.trim();
  const match = trimmed.match(/^url\(["']?([^"')]+)["']?\)/i);
  if (match && match[1]) return match[1];
  return trimmed;
}

function slugify(str) {
  return (
    String(str || "")
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "") || `item-${Date.now()}`
  );
}

/* ---------------------------- Hash & History Router ---------------------------- */

function parseHashRoute(hashStr) {
  const cleaned = (hashStr || "").replace(/^#\/?/, "").trim();
  if (!cleaned) return { page: "home", itemId: null };
  const parts = cleaned.split("/").filter(Boolean);
  const first = parts[0];
  const second = parts[1] ? decodeURIComponent(parts[1]) : null;

  if (first === "series") {
    return second ? { page: "seriesDetail", itemId: second } : { page: "series", itemId: null };
  }
  if (first === "projects") {
    return second ? { page: "projectDetail", itemId: second } : { page: "projects", itemId: null };
  }
  if (first === "news") {
    return second ? { page: "newsDetail", itemId: second } : { page: "news", itemId: null };
  }
  if (first === "merch") {
    return { page: "merch", itemId: null };
  }
  if (first === "p" && second) {
    return { page: "customPage", itemId: second };
  }
  if (first === "admin") {
    return { page: "admin", itemId: null };
  }
  return { page: "home", itemId: null };
}

function buildHashUrl(page, id) {
  if (page === "series") return "#/series";
  if (page === "seriesDetail" && id) return `#/series/${encodeURIComponent(id)}`;
  if (page === "projects") return "#/projects";
  if (page === "projectDetail" && id) return `#/projects/${encodeURIComponent(id)}`;
  if (page === "news") return "#/news";
  if (page === "newsDetail" && id) return `#/news/${encodeURIComponent(id)}`;
  if (page === "merch") return "#/merch";
  if (page === "customPage" && id) return `#/p/${encodeURIComponent(id)}`;
  if (page === "admin") return "#/admin";
  return "#/";
}

/* ---------------------------------- UI atoms ---------------------------------- */

function Mark({ size = 28, logoUrl = "", cyan = TOKENS.cyan, magenta = TOKENS.magenta, ink = TOKENS.ink, bg = TOKENS.bg }) {
  if (logoUrl) {
    return (
      <img
        src={extractRawImageUrl(logoUrl)}
        alt="TWSTD Logo"
        style={{
          width: size * 1.15,
          height: size * 1.15,
          objectFit: "contain",
          display: "block",
        }}
      />
    );
  }
  return (
    <svg width={size} height={size * 1.05} viewBox="0 0 40 42" fill="none" aria-hidden="true">
      <path
        d="M20 2c8 0 14 5 14 13 0 12-6 25-14 25S6 27 6 15C6 7 12 2 20 2Z"
        stroke={magenta}
        strokeWidth="1.4"
        transform="translate(1,1)"
      />
      <path
        d="M20 2c8 0 14 5 14 13 0 12-6 25-14 25S6 27 6 15C6 7 12 2 20 2Z"
        stroke={cyan}
        strokeWidth="1.4"
        transform="translate(-1,-1)"
      />
      <path d="M20 2c8 0 14 5 14 13 0 12-6 25-14 25S6 27 6 15C6 7 12 2 20 2Z" fill={ink} />
      <path d="M12 17c2-3 5-3 6 0" stroke={bg} strokeWidth="1.6" fill="none" strokeLinecap="round" />
      <path d="M22 17c2-3 5-3 6 0" stroke={bg} strokeWidth="1.6" fill="none" strokeLinecap="round" />
      <path d="M13 25c3 4 11 4 14 0" stroke={bg} strokeWidth="1.8" fill="none" strokeLinecap="round" />
    </svg>
  );
}

function Glitch({ children, as: Tag = "span", className = "" }) {
  return (
    <Tag className={`glitch ${className}`} data-text={typeof children === "string" ? children : undefined}>
      {children}
    </Tag>
  );
}

function Eyebrow({ children, style }) {
  return (
    <div className="eyebrow" style={style}>
      {children}
    </div>
  );
}

function ExternalLinksBar({ links, compact = false, glitchButtons = false }) {
  if (!Array.isArray(links) || links.length === 0) return null;
  return (
    <div className={`ext-links ${compact ? "is-compact" : ""}`}>
      {links.map((lnk, idx) => {
        if (!lnk || !lnk.url) return null;
        const isPrimary = idx === 0 || lnk.style === "primary";
        const label = lnk.label || "Ver sitio";
        return (
          <a
            key={idx}
            href={lnk.url}
            target="_blank"
            rel="noopener noreferrer"
            data-text={`${label} ↗`}
            className={`btn ${isPrimary ? "btn-primary" : "btn-ghost"} ${compact ? "btn-sm" : ""} ${
              glitchButtons ? "btn-glitch" : ""
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            {label} ↗
          </a>
        );
      })}
    </div>
  );
}

function FilesSection({ files }) {
  if (!Array.isArray(files) || files.length === 0) return null;
  return (
    <section className="files-section">
      <Eyebrow>Archivos y descargas</Eyebrow>
      <div className="files-grid">
        {files.map((f, i) => (
          <a
            key={i}
            href={f.url}
            target="_blank"
            rel="noopener noreferrer"
            download={f.name || true}
            className="file-card"
          >
            <div className="file-card-info">
              <span className="file-card-name">{f.name || "Archivo adjunto"}</span>
              {f.desc && <span className="file-card-desc">{f.desc}</span>}
            </div>
            <span className="file-card-action">Descargar ↓</span>
          </a>
        ))}
      </div>
    </section>
  );
}

/* --------------------------------- Nav ---------------------------------- */

function Nav({ page, itemId, go, customPages, settings }) {
  const baseItems = [
    { id: "home", label: "INICIO", active: page === "home" },
    { id: "series", label: "SERIES", active: page === "series" || page === "seriesDetail" },
    { id: "projects", label: "PROYECTOS", active: page === "projects" || page === "projectDetail" },
    { id: "merch", label: "MERCH", active: page === "merch" },
  ];

  const navCustomPages = (customPages || []).filter((cp) => cp.showInNav);
  const styleTokens = (settings && settings.style) || TOKENS;

  return (
    <header className="nav">
      <button className="brand" onClick={() => go("home")} aria-label="Ir al inicio">
        <Mark
          logoUrl={settings && settings.brandLogoUrl}
          cyan={styleTokens.cyan}
          magenta={styleTokens.magenta}
          ink={styleTokens.ink}
          bg={styleTokens.bg}
        />
        <span className="brand-word">
          {(settings && settings.brandTitle) || "TWSTD"}
          <span className="brand-word-sub">
            {(settings && settings.brandSub) || "PRODUCTIONS"}
          </span>
        </span>
      </button>
      <nav className="nav-links">
        {baseItems.map((it) => (
          <button
            key={it.id}
            className={`nav-link ${it.active ? "is-active" : ""}`}
            onClick={() => go(it.id)}
          >
            {it.label}
          </button>
        ))}
        {navCustomPages.map((cp) => (
          <button
            key={`cp-${cp.id}`}
            className={`nav-link ${page === "customPage" && itemId === cp.id ? "is-active" : ""}`}
            onClick={() => go("customPage", cp.id)}
          >
            {(cp.navLabel || cp.title || cp.id).toUpperCase()}
          </button>
        ))}
      </nav>
    </header>
  );
}

/* --------------------------------- Home ---------------------------------- */

function HeroBannerCarousel({ banners = [] }) {
  const safeBanners =
    Array.isArray(banners) && banners.length > 0
      ? banners
      : DEFAULT_SITE_SETTINGS.heroBanners;
  const [idx, setIdx] = useState(0);
  const reduceMotion = useRef(
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false
  );

  useEffect(() => {
    setIdx(0);
    if (reduceMotion.current || safeBanners.length < 2) return;
    const t = setInterval(() => {
      setIdx((curr) => (curr + 1) % safeBanners.length);
    }, 4200);
    return () => clearInterval(t);
  }, [safeBanners.length]);

  return (
    <div className="hero-showcase">
      {safeBanners.map((bg, i) => (
        <div
          key={i}
          className="hero-showcase-layer"
          style={{
            background: formatBannerBg(bg),
            opacity: i === idx ? 1 : 0,
          }}
        />
      ))}
      <div className="hero-showcase-scanlines" />
      <div className="hero-showcase-scrim" />
      {safeBanners.length > 1 && (
        <div className="hero-showcase-dots">
          {safeBanners.map((_, i) => (
            <button
              key={i}
              type="button"
              aria-label={`Banner ${i + 1}`}
              className={`dot-btn ${i === idx ? "is-active" : ""}`}
              onClick={() => setIdx(i)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function Home({ go, content }) {
  const { settings, series, news } = content;
  return (
    <div className="page">
      <section className="hero hero-split">
        <div className="hero-left">
          <Eyebrow>{settings.heroEyebrow || "Casa productora independiente"}</Eyebrow>
          {settings.heroLogoUrl ? (
            <div className="hero-custom-logo-wrap">
              <img
                src={extractRawImageUrl(settings.heroLogoUrl)}
                alt={settings.heroTitle || "TWSTD PRODUCTIONS"}
                className="hero-custom-logo"
              />
            </div>
          ) : (
            <h1 className="hero-title">
              <Glitch>{settings.heroTitle || "TWSTD PRODUCTIONS"}</Glitch>
            </h1>
          )}
          <p className="hero-sub">
            {settings.heroSub || "Casa productora de contenido multimedia. Producciones 100% mexicanas."}
          </p>
          <div className="hero-actions">
            <button
              className="btn btn-primary btn-glitch"
              data-text="Ver series"
              onClick={() => go("series")}
            >
              Ver series
            </button>
            <button
              className="btn btn-ghost btn-glitch"
              data-text="Ver proyectos"
              onClick={() => go("projects")}
            >
              Ver proyectos
            </button>
            {Array.isArray(settings.socialLinks) &&
              settings.socialLinks.map((lnk, idx) =>
                lnk && lnk.url ? (
                  <a
                    key={idx}
                    href={lnk.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-text={`${lnk.label || "Enlace"} ↗`}
                    className="btn btn-ghost btn-glitch"
                  >
                    {lnk.label || "Enlace"} ↗
                  </a>
                ) : null
              )}
          </div>
        </div>

        <div className="hero-right">
          <HeroBannerCarousel banners={settings.heroBanners} />
        </div>
      </section>

      <section className="strip">
        <div className="strip-head">
          <Eyebrow>Series producidas</Eyebrow>
          <button
            className="link-more btn-glitch-inline"
            data-text="Ver todas →"
            onClick={() => go("series")}
          >
            Ver todas →
          </button>
        </div>
        <div className="strip-row">
          {series.map((s) => (
            <SeriesCard
              key={s.id}
              s={s}
              onClick={() => go("seriesDetail", s.id)}
              compact
              glitchCard
            />
          ))}
        </div>
      </section>

      {news && news.length > 0 && (
        <section className="strip">
          <div className="strip-head">
            <Eyebrow>Noticias</Eyebrow>
            <button
              className="link-more btn-glitch-inline"
              data-text="Ver todas →"
              onClick={() => go("news")}
            >
              Ver todas →
            </button>
          </div>
          <div className="strip-row">
            {news.map((n) => (
              <div
                key={n.id}
                role="button"
                tabIndex={0}
                className="card series-card is-compact card-glitch"
                onClick={() => go("newsDetail", n.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    go("newsDetail", n.id);
                  }
                }}
              >
                <div
                  className="series-card-banner"
                  style={{ background: formatBannerBg(n.banners && n.banners[0]) }}
                />
                <div className="series-card-body">
                  <Eyebrow>{n.date ? `${n.eyebrow || "Noticia"} · ${n.date}` : n.eyebrow || "Noticia"}</Eyebrow>
                  <h3 className="series-card-title">
                    <span className="glitch-subtle" data-text={n.title}>
                      {n.title}
                    </span>
                  </h3>
                  <p className="series-card-tagline">{n.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

/* ------------------------------ Series list ------------------------------ */

function getSeriesCssVars(palette) {
  const p = { ...DEFAULT_SERIES_PALETTE, ...(palette || {}) };
  return {
    "--s-accent1": p.accent1 || TOKENS.cyan,
    "--s-accent2": p.accent2 || TOKENS.magenta,
    "--s-card-bg": p.cardBg || TOKENS.panel,
    "--s-text": p.text || TOKENS.ink,
  };
}

function SeriesCard({ s, onClick, compact, glitchCard = false }) {
  const cssVars = getSeriesCssVars(s.palette);
  // First image (s.banners[0]) is used as the thumbnail on the series button in Home (#/) and Series (#/series)
  const bannerBg = formatBannerBg(s.banners && s.banners[0]);

  return (
    <div
      role="button"
      tabIndex={0}
      style={cssVars}
      className={`card series-card has-series-palette ${compact ? "is-compact" : ""} ${
        glitchCard ? "card-glitch" : ""
      }`}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <div className="series-card-banner" style={{ background: bannerBg }}>
        {s.logoUrl && (
          <div className="series-card-logo-overlay">
            <img src={extractRawImageUrl(s.logoUrl)} alt={s.title} />
          </div>
        )}
      </div>
      <div className="series-card-body">
        <Eyebrow style={{ color: "var(--s-accent1)" }}>{s.eyebrow}</Eyebrow>
        <h3 className="series-card-title" style={{ color: "var(--s-text)" }}>
          <span className="glitch-subtle" data-text={s.title}>
            {s.title}
          </span>
        </h3>
        {s.meta && compact && <p className="series-card-meta-sm">{s.meta}</p>}
        <p className="series-card-tagline">{s.tagline}</p>
        {!compact && s.links && s.links.length > 0 && (
          <div style={{ marginTop: 14 }}>
            <ExternalLinksBar links={s.links} compact />
          </div>
        )}
      </div>
    </div>
  );
}

function SeriesList({ go, series }) {
  return (
    <div className="page">
      <section className="section-head">
        <Eyebrow>Catálogo</Eyebrow>
        <h2 className="section-title">Series producidas</h2>
        <p className="section-sub">
          ¡Todas las series producidas por Twstd Productions!
        </p>
      </section>
      <section className="grid">
        {series.map((s) => (
          <SeriesCard key={s.id} s={s} onClick={() => go("seriesDetail", s.id)} glitchCard />
        ))}
      </section>
    </div>
  );
}

/* ----------------------------- Detail View (Series / Project / Custom) ----------------------------- */

function MediaDetailView({
  backLabel,
  onBack,
  eyebrow,
  title,
  logoUrl,
  meta,
  statusBadge,
  isPlaceholder,
  synopsis,
  banners = [],
  palette,
  links = [],
  files = [],
  seriesMerch = [],
  settings,
  onGoMerch,
}) {
  const safeBanners =
    Array.isArray(banners) && banners.length > 0
      ? banners
      : ["linear-gradient(135deg, #0a0a0c 0%, #1a1a1e 50%, #2a2a30 100%)"];
  const [bannerIndex, setBannerIndex] = useState(0);
  const reduceMotion = useRef(
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false
  );

  useEffect(() => {
    setBannerIndex(0);
    if (reduceMotion.current || safeBanners.length < 2) return;
    const t = setInterval(() => {
      setBannerIndex((i) => (i + 1) % safeBanners.length);
    }, 5000);
    return () => clearInterval(t);
  }, [title, safeBanners.length]);

  const cssVars = getSeriesCssVars(palette);

  return (
    <div className="page detail-themed" style={cssVars}>
      {onBack && (
        <button className="back-link" onClick={onBack}>
          ← {backLabel || "Volver"}
        </button>
      )}

      <section className="banner detail-banner">
        {safeBanners.map((bg, i) => (
          <div
            key={i}
            className="banner-layer"
            style={{ background: formatBannerBg(bg), opacity: i === bannerIndex ? 1 : 0 }}
          />
        ))}
        <div className="banner-scrim" />
        <div className="banner-content">
          <div className="banner-badges">
            {isPlaceholder && <span className="badge detail-badge">En Producción</span>}
            {statusBadge && (
              <span
                className={`status ${
                  statusBadge === "En desarrollo" || statusBadge === "Activo" ? "status-dev" : "status-proto"
                }`}
              >
                {statusBadge}
              </span>
            )}
          </div>
          {eyebrow && <Eyebrow style={{ color: "var(--s-accent1)" }}>{eyebrow}</Eyebrow>}
          {logoUrl ? (
            <div className="banner-custom-logo">
              <img src={extractRawImageUrl(logoUrl)} alt={title} />
            </div>
          ) : (
            <h1 className="banner-logo" style={{ color: "var(--s-text)" }}>
              <Glitch>{title}</Glitch>
            </h1>
          )}
          {meta && <p className="banner-meta">{meta}</p>}
          {synopsis && <p className="banner-synopsis">{synopsis}</p>}
          {links && links.length > 0 && (
            <div style={{ marginTop: 22 }}>
              <ExternalLinksBar links={links} glitchButtons />
            </div>
          )}
        </div>
        {safeBanners.length > 1 && (
          <div className="banner-dots">
            {safeBanners.map((_, i) => (
              <button
                key={i}
                aria-label={`Imagen ${i + 1}`}
                className={`dot-btn ${i === bannerIndex ? "is-active" : ""}`}
                onClick={() => setBannerIndex(i)}
              />
            ))}
          </div>
        )}
      </section>

      <FilesSection files={files} />

      {safeBanners.length > 0 && (
        <section className="gallery">
          <Eyebrow style={{ color: "var(--s-accent1)" }}>Imágenes y galería</Eyebrow>
          <div className="gallery-grid">
            {safeBanners.map((bg, i) => (
              <button
                key={i}
                type="button"
                className={`gallery-tile ${i === bannerIndex ? "is-selected" : ""}`}
                style={{ background: formatBannerBg(bg) }}
                onClick={() => setBannerIndex(i)}
                aria-label={`Ver imagen ${i + 1}`}
              />
            ))}
          </div>
        </section>
      )}

      {Array.isArray(seriesMerch) && seriesMerch.length > 0 && (
        <section className="series-merch-section">
          <div className="strip-head">
            <Eyebrow style={{ color: "var(--s-accent1)" }}>Merch oficial · {title}</Eyebrow>
            {onGoMerch && (
              <button className="link-more" onClick={onGoMerch}>
                Ver toda la tienda →
              </button>
            )}
          </div>
          <div className="merch-grid">
            {seriesMerch.map((m) => (
              <MerchCard key={m.id} m={m} settings={settings} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function SeriesDetail({ id, go, series, merch, settings }) {
  const s = series.find((x) => x.id === id) || series[0];
  if (!s) {
    return (
      <div className="page">
        <button className="back-link" onClick={() => go("series")}>
          ← Series
        </button>
        <p>No hay series disponibles.</p>
      </div>
    );
  }
  // Exclude the first image (s.banners[0]), which is strictly the thumbnail for the series shortcut button
  const detailBanners =
    Array.isArray(s.banners) && s.banners.length > 1
      ? s.banners.slice(1)
      : s.banners || [];

  const seriesMerch = (merch || []).filter((m) => m.seriesId === s.id);

  return (
    <MediaDetailView
      backLabel="Series"
      onBack={() => go("series")}
      eyebrow={s.eyebrow}
      title={s.title}
      logoUrl={s.logoUrl}
      meta={s.meta}
      isPlaceholder={s.isPlaceholder}
      synopsis={s.synopsis}
      banners={detailBanners}
      palette={s.palette}
      links={s.links}
      files={s.files}
      seriesMerch={seriesMerch}
      settings={settings}
      onGoMerch={() => go("merch")}
    />
  );
}

/* -------------------------------- Projects -------------------------------- */

function Projects({ go, projects }) {
  return (
    <div className="page">
      <section className="section-head">
        <Eyebrow>Catálogo</Eyebrow>
        <h2 className="section-title">Proyectos propios</h2>
        <p className="section-sub">
          Cosas que TWSTD desarrolla y posee de principio a fin, fuera del formato serie. Haz clic en cualquier proyecto para ver su página completa, archivos y enlaces.
        </p>
      </section>
      <section className="proj-list">
        {projects.map((p) => (
          <article
            key={p.id}
            role="button"
            tabIndex={0}
            className="proj-card is-clickable card-glitch"
            onClick={() => go("projectDetail", p.id)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                go("projectDetail", p.id);
              }
            }}
          >
            {p.banners && p.banners[0] && (
              <div
                className="proj-card-thumb"
                style={{ background: formatBannerBg(p.banners[0]) }}
              />
            )}
            <div className="proj-card-main">
              <div className="proj-card-top">
                <h3 className="proj-title">
                  <span className="glitch-subtle" data-text={p.title}>
                    {p.title}
                  </span>
                </h3>
                {p.status && (
                  <span
                    className={`status status-${
                      p.status === "En desarrollo" || p.status === "Activo" ? "dev" : "proto"
                    }`}
                  >
                    {p.status}
                  </span>
                )}
              </div>
              {p.type && <p className="proj-type">{p.type}</p>}
              <p className="proj-desc">{p.desc}</p>
              <div className="proj-card-footer">
                <span className="proj-engine">{p.engine}</span>
                <div className="proj-card-actions">
                  <ExternalLinksBar links={p.links} compact glitchButtons />
                  <span className="link-more">Ver detalles →</span>
                </div>
              </div>
            </div>
          </article>
        ))}
      </section>
    </div>
  );
}

function ProjectDetail({ id, go, projects }) {
  const p = projects.find((x) => x.id === id) || projects[0];
  if (!p) {
    return (
      <div className="page">
        <button className="back-link" onClick={() => go("projects")}>
          ← Proyectos
        </button>
        <p>Proyecto no encontrado.</p>
      </div>
    );
  }
  return (
    <MediaDetailView
      backLabel="Proyectos"
      onBack={() => go("projects")}
      eyebrow={p.type || "Proyecto original"}
      title={p.title}
      logoUrl={p.logoUrl}
      meta={p.engine}
      statusBadge={p.status}
      synopsis={p.synopsis || p.desc}
      banners={p.banners}
      links={p.links}
      files={p.files}
    />
  );
}

/* ---------------------------------- News ---------------------------------- */

function NewsList({ go, news }) {
  return (
    <div className="page">
      <section className="section-head">
        <Eyebrow>Actualizaciones</Eyebrow>
        <h2 className="section-title">Noticias</h2>
        <p className="section-sub">
          Novedades, anuncios de producción y comunicados oficiales de TWSTD Productions.
        </p>
      </section>
      <section className="proj-list">
        {(news || []).map((n) => (
          <article
            key={n.id}
            role="button"
            tabIndex={0}
            className="proj-card is-clickable card-glitch"
            onClick={() => go("newsDetail", n.id)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                go("newsDetail", n.id);
              }
            }}
          >
            {n.banners && n.banners[0] && (
              <div
                className="proj-card-thumb"
                style={{ background: formatBannerBg(n.banners[0]) }}
              />
            )}
            <div className="proj-card-main">
              <div className="proj-card-top">
                <h3 className="proj-title">
                  <span className="glitch-subtle" data-text={n.title}>
                    {n.title}
                  </span>
                </h3>
                {n.date && <span className="status status-dev">{n.date}</span>}
              </div>
              {n.eyebrow && <p className="proj-type">{n.eyebrow}</p>}
              <p className="proj-desc">{n.desc}</p>
              <div className="proj-card-footer">
                <span className="proj-engine">{n.date || ""}</span>
                <div className="proj-card-actions">
                  <ExternalLinksBar links={n.links} compact glitchButtons />
                  <span className="link-more">Leer noticia →</span>
                </div>
              </div>
            </div>
          </article>
        ))}
      </section>
    </div>
  );
}

function NewsDetail({ id, go, news }) {
  const n = (news || []).find((x) => x.id === id) || (news && news[0]);
  if (!n) {
    return (
      <div className="page">
        <button className="back-link" onClick={() => go("news")}>
          ← Noticias
        </button>
        <p>Noticia no encontrada.</p>
      </div>
    );
  }
  return (
    <MediaDetailView
      backLabel="Noticias"
      onBack={() => go("news")}
      eyebrow={n.eyebrow || "Noticia"}
      title={n.title}
      meta={n.date}
      synopsis={n.synopsis || n.desc}
      banners={n.banners}
      links={n.links}
      files={n.files}
    />
  );
}

/* ---------------------------------- Merch ---------------------------------- */

function MerchCard({ m, settings }) {
  return (
    <article className="merch-card">
      <div
        className="merch-thumb"
        style={
          m.image
            ? { background: formatBannerBg(m.image) }
            : undefined
        }
      >
        {!m.image && <Mark size={40} logoUrl={settings && settings.brandLogoUrl} />}
      </div>
      <div className="merch-info">
        <span className="eyebrow" style={{ color: "var(--s-accent1)" }}>
          {m.tag}
        </span>
        <h3 className="merch-title">{m.title}</h3>
        <div className="merch-row">
          <span className="merch-price">{m.price}</span>
          {m.url ? (
            <a
              href={m.url}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary btn-sm"
            >
              {m.buttonLabel || "Comprar"} ↗
            </a>
          ) : (
            <span className="merch-soon">{m.status || "Próximamente"}</span>
          )}
        </div>
      </div>
    </article>
  );
}

function Merch({ merch = [], series = [], settings, go }) {
  const knownSeriesIds = new Set(series.map((s) => s.id));
  const studioMerch = merch.filter((m) => !m.seriesId || !knownSeriesIds.has(m.seriesId));
  const seriesGroups = series
    .map((s) => ({
      series: s,
      items: merch.filter((m) => m.seriesId === s.id),
    }))
    .filter((g) => g.items.length > 0);

  return (
    <div className="page">
      <section className="section-head">
        <Eyebrow>Tienda</Eyebrow>
        <h2 className="section-title">Merch</h2>
        <p className="section-sub">
          Objetos oficiales del estudio y colecciones divididas por cada serie.
        </p>
        {settings && settings.merchStoreUrl && (
          <div style={{ marginTop: 18 }}>
            <a
              href={settings.merchStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-text={`${settings.merchStoreLabel || "Ir a la tienda oficial"} ↗`}
              className="btn btn-primary btn-glitch"
            >
              {settings.merchStoreLabel || "Ir a la tienda oficial"} ↗
            </a>
          </div>
        )}
      </section>

      {studioMerch.length > 0 && (
        <section className="merch-group-section">
          <div className="strip-head">
            <div>
              <Eyebrow>Colección general</Eyebrow>
              <h3 className="merch-group-title">TWSTD Productions · Estudio</h3>
            </div>
          </div>
          <div className="merch-grid">
            {studioMerch.map((m) => (
              <MerchCard key={m.id} m={m} settings={settings} />
            ))}
          </div>
        </section>
      )}

      {seriesGroups.map(({ series: s, items }) => (
        <section
          key={s.id}
          className="merch-group-section"
          style={getSeriesCssVars(s.palette)}
        >
          <div className="strip-head">
            <div>
              <Eyebrow style={{ color: "var(--s-accent1)" }}>
                Merch de la serie
              </Eyebrow>
              <h3 className="merch-group-title" style={{ color: "var(--s-text)" }}>
                {s.title}
              </h3>
            </div>
            {go && (
              <button
                type="button"
                className="link-more"
                style={{ color: "var(--s-accent1)" }}
                onClick={() => go("seriesDetail", s.id)}
              >
                Ver serie →
              </button>
            )}
          </div>
          <div className="merch-grid">
            {items.map((m) => (
              <MerchCard key={m.id} m={m} settings={settings} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

/* ------------------------------ Custom Page ------------------------------ */

function CustomPageView({ slug, go, customPages }) {
  const cp = (customPages || []).find((x) => x.id === slug);
  if (!cp) {
    return (
      <div className="page">
        <button className="back-link" onClick={() => go("home")}>
          ← Inicio
        </button>
        <section className="section-head">
          <Eyebrow>404</Eyebrow>
          <h2 className="section-title">Página no encontrada</h2>
        </section>
      </div>
    );
  }
  return (
    <MediaDetailView
      backLabel="Inicio"
      onBack={() => go("home")}
      eyebrow={cp.eyebrow || "TWSTD Productions"}
      title={cp.title}
      meta={cp.subtitle}
      synopsis={cp.body}
      banners={cp.banners}
      links={cp.links}
      files={cp.files}
    />
  );
}

/* ----------------------------- Admin Editors ----------------------------- */

function ColorField({ label, value, onChange }) {
  const safeHex = /^#[0-9a-fA-F]{6}$/.test(value || "") ? value : "#0a0a0c";
  return (
    <label className="admin-field">
      <span>{label}</span>
      <div className="admin-color-row">
        <input
          type="color"
          className="admin-color-swatch"
          value={safeHex}
          onChange={(e) => onChange(e.target.value)}
        />
        <input
          type="text"
          className="admin-input"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
    </label>
  );
}

function SeriesPaletteEditor({ palette, onChange }) {
  const p = { ...DEFAULT_SERIES_PALETTE, ...(palette || {}) };
  function updateColor(key, val) {
    onChange({ ...p, [key]: val });
  }
  function resetPalette() {
    onChange({ ...DEFAULT_SERIES_PALETTE });
  }
  return (
    <div className="admin-subbox">
      <div className="admin-subbox-head">
        <span className="eyebrow" style={{ marginBottom: 0 }}>
          Paleta de colores exclusiva de esta serie (afecta tarjeta en Inicio y página de la serie)
        </span>
        <button type="button" className="btn btn-ghost btn-sm" onClick={resetPalette}>
          Restaurar colores TWSTD
        </button>
      </div>
      <div className="admin-form-grid">
        <ColorField
          label="Color Acento 1 (Principal / Glitch 1 / Bordes)"
          value={p.accent1}
          onChange={(val) => updateColor("accent1", val)}
        />
        <ColorField
          label="Color Acento 2 (Glitch 2 / Sombra)"
          value={p.accent2}
          onChange={(val) => updateColor("accent2", val)}
        />
        <ColorField
          label="Fondo de tarjeta"
          value={p.cardBg}
          onChange={(val) => updateColor("cardBg", val)}
        />
        <ColorField
          label="Color de título"
          value={p.text}
          onChange={(val) => updateColor("text", val)}
        />
      </div>
    </div>
  );
}

function LinksEditor({ links = [], onChange, placeholderLabel = "Ver en YouTube" }) {
  function updateLink(idx, field, val) {
    const next = links.map((l, i) => (i === idx ? { ...l, [field]: val } : l));
    onChange(next);
  }
  function addLink() {
    onChange([...links, { label: placeholderLabel, url: "", style: links.length === 0 ? "primary" : "ghost" }]);
  }
  function removeLink(idx) {
    onChange(links.filter((_, i) => i !== idx));
  }

  return (
    <div className="admin-subbox">
      <div className="admin-subbox-head">
        <span className="eyebrow" style={{ marginBottom: 0 }}>
          Sitios conectados / Botones externos (ej. "Ver en YouTube", "Jugar en Steam")
        </span>
        <button type="button" className="btn btn-ghost btn-sm" onClick={addLink}>
          + Añadir botón
        </button>
      </div>
      {links.length === 0 && (
        <p className="admin-hint">Sin botones conectados todavía.</p>
      )}
      {links.map((lnk, idx) => (
        <div key={idx} className="admin-inline-row">
          <input
            className="admin-input"
            placeholder="Texto (ej. Ver en YouTube)"
            value={lnk.label || ""}
            onChange={(e) => updateLink(idx, "label", e.target.value)}
          />
          <input
            className="admin-input"
            placeholder="URL (https://...)"
            value={lnk.url || ""}
            onChange={(e) => updateLink(idx, "url", e.target.value)}
          />
          <select
            className="admin-input admin-select-sm"
            value={lnk.style || "primary"}
            onChange={(e) => updateLink(idx, "style", e.target.value)}
          >
            <option value="primary">Destacado</option>
            <option value="ghost">Secundario</option>
          </select>
          <button
            type="button"
            className="btn btn-danger btn-sm"
            onClick={() => removeLink(idx)}
          >
            ✕
          </button>
        </div>
      ))}
    </div>
  );
}

function BannersEditor({
  banners = [],
  onChange,
  onUploadFile,
  title = "Imágenes de Banner y Galería",
}) {
  const [uploading, setUploading] = useState(false);

  function updateBanner(idx, val) {
    const next = banners.map((b, i) => (i === idx ? val : b));
    onChange(next);
  }
  function addBannerUrl() {
    onChange([...banners, ""]);
  }
  function removeBanner(idx) {
    onChange(banners.filter((_, i) => i !== idx));
  }
  function moveBanner(idx, dir) {
    const target = idx + dir;
    if (target < 0 || target >= banners.length) return;
    const copy = [...banners];
    const temp = copy[idx];
    copy[idx] = copy[target];
    copy[target] = temp;
    onChange(copy);
  }
  async function handleFileSelect(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await onUploadFile(file);
      if (res && res.url) {
        onChange([...banners, res.url]);
      }
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }

  return (
    <div className="admin-subbox">
      <div className="admin-subbox-head">
        <span className="eyebrow" style={{ marginBottom: 0 }}>
          {title}
        </span>
        <div className="admin-btn-group">
          <label className="btn btn-primary btn-sm" style={{ cursor: "pointer" }}>
            {uploading ? "Subiendo..." : "↑ Subir imagen"}
            <input
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleFileSelect}
              disabled={uploading}
            />
          </label>
          <button type="button" className="btn btn-ghost btn-sm" onClick={addBannerUrl}>
            + Pegar URL / CSS
          </button>
        </div>
      </div>
      {banners.length === 0 && (
        <p className="admin-hint">Sin imágenes configuradas.</p>
      )}
      <div className="admin-banners-list">
        {banners.map((b, idx) => (
          <div key={idx} className="admin-banner-row">
            <div
              className="admin-banner-preview"
              style={{ background: formatBannerBg(b) }}
              title={idx === 0 ? "Imagen principal (portada)" : `Imagen ${idx + 1}`}
            />
            <input
              className="admin-input"
              placeholder="URL de imagen (https://... o uploads/...) o gradiente CSS"
              value={b}
              onChange={(e) => updateBanner(idx, e.target.value)}
            />
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => moveBanner(idx, -1)}
              disabled={idx === 0}
              title="Subir orden"
            >
              ↑
            </button>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => moveBanner(idx, 1)}
              disabled={idx === banners.length - 1}
              title="Bajar orden"
            >
              ↓
            </button>
            <button
              type="button"
              className="btn btn-danger btn-sm"
              onClick={() => removeBanner(idx)}
              title="Eliminar imagen"
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function FilesEditor({ files = [], onChange, onUploadFile }) {
  const [uploading, setUploading] = useState(false);

  function updateFile(idx, field, val) {
    const next = files.map((f, i) => (i === idx ? { ...f, [field]: val } : f));
    onChange(next);
  }
  function addEmptyFile() {
    onChange([...files, { name: "Documento / Archivo", url: "", desc: "" }]);
  }
  function removeFile(idx) {
    onChange(files.filter((_, i) => i !== idx));
  }
  async function handleUpload(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await onUploadFile(file);
      if (res && res.url) {
        onChange([
          ...files,
          { name: res.name || file.name, url: res.url, desc: "" },
        ]);
      }
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  }

  return (
    <div className="admin-subbox">
      <div className="admin-subbox-head">
        <span className="eyebrow" style={{ marginBottom: 0 }}>
          Archivos descargables (PDF, ZIP, Presskit, etc.)
        </span>
        <div className="admin-btn-group">
          <label className="btn btn-primary btn-sm" style={{ cursor: "pointer" }}>
            {uploading ? "Subiendo..." : "↑ Subir archivo"}
            <input
              type="file"
              style={{ display: "none" }}
              onChange={handleUpload}
              disabled={uploading}
            />
          </label>
          <button type="button" className="btn btn-ghost btn-sm" onClick={addEmptyFile}>
            + Enlace a archivo
          </button>
        </div>
      </div>
      {files.length === 0 && (
        <p className="admin-hint">Sin archivos adjuntos.</p>
      )}
      {files.map((f, idx) => (
        <div key={idx} className="admin-inline-row">
          <input
            className="admin-input"
            placeholder="Nombre (ej. Presskit PDF)"
            value={f.name || ""}
            onChange={(e) => updateFile(idx, "name", e.target.value)}
          />
          <input
            className="admin-input"
            placeholder="Descripción breve (opcional)"
            value={f.desc || ""}
            onChange={(e) => updateFile(idx, "desc", e.target.value)}
          />
          <input
            className="admin-input"
            placeholder="URL del archivo"
            value={f.url || ""}
            onChange={(e) => updateFile(idx, "url", e.target.value)}
          />
          <button
            type="button"
            className="btn btn-danger btn-sm"
            onClick={() => removeFile(idx)}
          >
            ✕
          </button>
        </div>
      ))}
    </div>
  );
}

function AdminDashboard({ content, onLiveChange, onSaveContent, serverAvailable, go }) {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [isAuthed, setIsAuthed] = useState(false);
  const [passwordInput, setPasswordInput] = useState("");
  const [authError, setAuthError] = useState("");
  const [activeTab, setActiveTab] = useState("style");
  const [draft, setDraft] = useState(() => normalizeContent(content));
  const [saveNotice, setSaveNotice] = useState("");
  const [saving, setSaving] = useState(false);

  // Password change states
  const [currPass, setCurrPass] = useState("");
  const [newPass, setNewPass] = useState("");
  const [passMsg, setPassMsg] = useState("");

  // Keep draft synced and immediately reflect edits on Homepage & Series cards
  function applyDraft(nextDraft) {
    const normalized = normalizeContent(nextDraft);
    setDraft(normalized);
    onLiveChange(normalized);
  }

  useEffect(() => {
    if (!token) return;
    if (token === "local-session") {
      setIsAuthed(true);
      return;
    }
    fetch("api.php?action=verify", {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-Admin-Token": token },
      body: JSON.stringify({ token }),
    })
      .then((r) => r.json())
      .then((res) => {
        if (res && res.ok) setIsAuthed(true);
      })
      .catch(() => {});
  }, [token]);

  async function handleLogin(e) {
    e.preventDefault();
    setAuthError("");
    try {
      const resp = await fetch("api.php?action=login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: passwordInput }),
      });
      const contentType = resp.headers.get("content-type") || "";
      if (contentType.includes("application/json")) {
        const data = await resp.json();
        if (data.ok && data.token) {
          sessionStorage.setItem(TOKEN_KEY, data.token);
          setToken(data.token);
          setIsAuthed(true);
          setPasswordInput("");
          return;
        } else {
          setAuthError(data.error || "Contraseña incorrecta.");
          return;
        }
      }
    } catch {
      // Fallback for localhost dev server where PHP is not running
    }

    const expectedLocalPass = localStorage.getItem(LOCAL_PASS_KEY) || "twstd-admin";
    if (passwordInput === expectedLocalPass) {
      sessionStorage.setItem(TOKEN_KEY, "local-session");
      setToken("local-session");
      setIsAuthed(true);
      setPasswordInput("");
    } else {
      setAuthError("Contraseña incorrecta.");
    }
  }

  function handleLogout() {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setIsAuthed(false);
  }

  async function handleUploadFile(file) {
    if (serverAvailable && token && token !== "local-session") {
      const form = new FormData();
      form.append("file", file);
      form.append("token", token);
      try {
        const resp = await fetch("api.php?action=upload", {
          method: "POST",
          headers: { "X-Admin-Token": token },
          body: form,
        });
        const data = await resp.json();
        if (data && data.ok) {
          return { url: data.url, name: data.name };
        }
        alert(data.error || "Error al subir archivo al servidor.");
        return null;
      } catch {
        alert("Error de red al subir el archivo.");
        return null;
      }
    } else {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
          resolve({ url: reader.result, name: file.name });
        };
        reader.readAsDataURL(file);
      });
    }
  }

  async function saveChanges(customDraft) {
    const toSave = normalizeContent(customDraft || draft);
    setSaving(true);
    setSaveNotice("");
    const result = await onSaveContent(toSave, token);
    setSaving(false);
    if (result.ok) {
      setSaveNotice(
        result.mode === "server"
          ? "✓ Guardado en vivo en el servidor cPanel"
          : "✓ Guardado en este navegador"
      );
      setTimeout(() => setSaveNotice(""), 4000);
    } else {
      setSaveNotice(`⚠ ${result.error || "Error al guardar"}`);
    }
  }

  async function handleChangePassword(e) {
    e.preventDefault();
    setPassMsg("");
    if (newPass.length < 4) {
      setPassMsg("La nueva contraseña debe tener al menos 4 caracteres.");
      return;
    }
    if (serverAvailable && token !== "local-session") {
      try {
        const resp = await fetch("api.php?action=change_password", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Admin-Token": token,
          },
          body: JSON.stringify({ currentPassword: currPass, newPassword: newPass }),
        });
        const data = await resp.json();
        if (data.ok) {
          setPassMsg("✓ Contraseña actualizada en el servidor.");
          setCurrPass("");
          setNewPass("");
        } else {
          setPassMsg(data.error || "No se pudo cambiar la contraseña.");
        }
        return;
      } catch {
        // fallback
      }
    }
    const currentLocal = localStorage.getItem(LOCAL_PASS_KEY) || "twstd-admin";
    if (currPass !== currentLocal) {
      setPassMsg("La contraseña actual no es correcta.");
      return;
    }
    localStorage.setItem(LOCAL_PASS_KEY, newPass);
    setPassMsg("✓ Contraseña local actualizada.");
    setCurrPass("");
    setNewPass("");
  }

  function exportJson() {
    const blob = new Blob([JSON.stringify(draft, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "twstd-content-backup.json";
    a.click();
    URL.revokeObjectURL(url);
  }

  function importJson(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result);
        const normalized = normalizeContent(parsed);
        applyDraft(normalized);
        saveChanges(normalized);
      } catch {
        alert("El archivo JSON no es válido.");
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  }

  if (!isAuthed) {
    return (
      <div className="page">
        <section className="admin-login-box">
          <Eyebrow>Acceso restringido</Eyebrow>
          <h2 className="section-title" style={{ marginTop: 0 }}>
            Panel de Administración
          </h2>
          <p className="section-sub" style={{ marginBottom: 24 }}>
            Ingresa la contraseña de administrador para editar el estilo del sitio, logos, series, proyectos y páginas.
          </p>
          <form onSubmit={handleLogin} className="admin-login-form">
            <input
              type="password"
              className="admin-input"
              placeholder="Contraseña de administrador"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              autoFocus
            />
            {authError && <p className="admin-error">{authError}</p>}
            <div className="hero-actions" style={{ marginTop: 16 }}>
              <button type="submit" className="btn btn-primary">
                Entrar al panel
              </button>
              <button type="button" className="btn btn-ghost" onClick={() => go("home")}>
                Cancelar
              </button>
            </div>
          </form>
        </section>
      </div>
    );
  }

  /* ---- Helpers to mutate draft sections ---- */
  function updateSettings(patch) {
    applyDraft({
      ...draft,
      settings: { ...draft.settings, ...patch },
    });
  }

  function updateSiteStyle(patch) {
    applyDraft({
      ...draft,
      settings: {
        ...draft.settings,
        style: { ...(draft.settings.style || {}), ...patch },
      },
    });
  }

  function updateProject(idx, patch) {
    const next = draft.projects.map((p, i) => (i === idx ? { ...p, ...patch } : p));
    applyDraft({ ...draft, projects: next });
  }
  function addProject() {
    const newProj = {
      id: `proyecto-${Date.now().toString().slice(-4)}`,
      title: "Nuevo Proyecto",
      type: "Videojuego / Producción",
      status: "En desarrollo",
      engine: "Unreal Engine 5 / YouTube",
      desc: "Descripción corta para la tarjeta del proyecto en Inicio y Proyectos.",
      synopsis: "Descripción detallada para la página individual del proyecto.",
      logoUrl: "",
      banners: ["linear-gradient(135deg, #0a0a0c 0%, #1a1a1e 50%, #2a2a30 100%)"],
      links: [{ label: "Ver en YouTube", url: "", style: "primary" }],
      files: [],
    };
    applyDraft({ ...draft, projects: [newProj, ...draft.projects] });
  }
  function removeProject(idx) {
    if (!window.confirm("¿Eliminar este proyecto?")) return;
    applyDraft({ ...draft, projects: draft.projects.filter((_, i) => i !== idx) });
  }

  function updateSeries(idx, patch) {
    const next = draft.series.map((s, i) => (i === idx ? { ...s, ...patch } : s));
    applyDraft({ ...draft, series: next });
  }
  function addSeries() {
    const newSer = {
      id: `serie-${Date.now().toString().slice(-4)}`,
      title: "Nueva Serie Original",
      eyebrow: "Serie original · terror",
      meta: "N.º de episodios · formato",
      tagline: "Frase corta que presenta la serie.",
      synopsis: "Sinopsis completa de la serie.",
      logoUrl: "",
      banners: [
        "linear-gradient(135deg, #0a0a0c 0%, #1a1a1e 50%, #2a2a30 100%)",
        "linear-gradient(135deg, #0a0a0c 0%, #22181e 50%, #3a2430 100%)",
      ],
      palette: { ...DEFAULT_SERIES_PALETTE },
      links: [{ label: "Ver en YouTube", url: "", style: "primary" }],
      files: [],
      isPlaceholder: false,
    };
    applyDraft({ ...draft, series: [newSer, ...draft.series] });
  }
  function removeSeries(idx) {
    if (!window.confirm("¿Eliminar esta serie?")) return;
    applyDraft({ ...draft, series: draft.series.filter((_, i) => i !== idx) });
  }

  function updateNews(idx, patch) {
    const next = (draft.news || []).map((n, i) => (i === idx ? { ...n, ...patch } : n));
    applyDraft({ ...draft, news: next });
  }
  function addNews() {
    const newEntry = {
      id: `noticia-${Date.now().toString().slice(-4)}`,
      title: "Nueva Noticia",
      eyebrow: "Anuncio · Estudio",
      date: "2026",
      desc: "Resumen corto que aparece en la barra de Noticias de la página de Inicio.",
      synopsis: "Contenido completo de la noticia.",
      banners: ["linear-gradient(135deg, #0a0a0c 0%, #1a1a1e 50%, #2a2a30 100%)"],
      links: [],
      files: [],
    };
    applyDraft({ ...draft, news: [newEntry, ...(draft.news || [])] });
  }
  function removeNews(idx) {
    if (!window.confirm("¿Eliminar esta noticia?")) return;
    applyDraft({ ...draft, news: (draft.news || []).filter((_, i) => i !== idx) });
  }

  function updateMerch(idx, patch) {
    const next = draft.merch.map((m, i) => (i === idx ? { ...m, ...patch } : m));
    applyDraft({ ...draft, merch: next });
  }
  function addMerch(defaultSeriesId = "") {
    const cleanSeriesId = typeof defaultSeriesId === "string" ? defaultSeriesId : "";
    const matchedSeries = draft.series.find((s) => s.id === cleanSeriesId);
    const newItem = {
      id: `merch-${Date.now().toString().slice(-4)}`,
      title: matchedSeries ? `Nuevo Artículo — ${matchedSeries.title}` : "Nuevo Artículo",
      price: "$––",
      tag: matchedSeries ? "Edición serie" : "Estudio",
      seriesId: cleanSeriesId,
      status: "Disponible",
      buttonLabel: "Comprar",
      image: "",
      url: "",
    };
    applyDraft({ ...draft, merch: [newItem, ...draft.merch] });
  }
  function removeMerch(idx) {
    if (!window.confirm("¿Eliminar este artículo de merch?")) return;
    applyDraft({ ...draft, merch: draft.merch.filter((_, i) => i !== idx) });
  }

  function updateCustomPage(idx, patch) {
    const next = draft.customPages.map((cp, i) => (i === idx ? { ...cp, ...patch } : cp));
    applyDraft({ ...draft, customPages: next });
  }
  function addCustomPage() {
    const slug = `pagina-${draft.customPages.length + 1}`;
    const newPage = {
      id: slug,
      title: "Nueva Página",
      navLabel: "NUEVA PÁGINA",
      showInNav: true,
      eyebrow: "TWSTD Productions",
      subtitle: "Subtítulo o información adicional",
      body: "Escribe aquí el contenido principal de tu nueva página.",
      banners: ["linear-gradient(135deg, #0a0a0c 0%, #131c22 50%, #1e3038 100%)"],
      links: [],
      files: [],
    };
    applyDraft({ ...draft, customPages: [...draft.customPages, newPage] });
  }
  function removeCustomPage(idx) {
    if (!window.confirm("¿Eliminar esta página personalizada?")) return;
    applyDraft({ ...draft, customPages: draft.customPages.filter((_, i) => i !== idx) });
  }

  return (
    <div className="page">
      <div className="admin-header">
        <div>
          <Eyebrow>
            Modo Administrador ·{" "}
            {serverAvailable && token !== "local-session"
              ? "Conectado a cPanel (api.php)"
              : "Guardado Instantáneo en Navegador"}
          </Eyebrow>
          <h2 className="section-title" style={{ margin: 0 }}>
            Dashboard de Estudio
          </h2>
        </div>
        <div className="admin-header-actions">
          {saveNotice && <span className="admin-save-notice">{saveNotice}</span>}
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => saveChanges()}
            disabled={saving}
          >
            {saving ? "Guardando..." : "Guardar cambios"}
          </button>
          <button type="button" className="btn btn-ghost" onClick={handleLogout}>
            Cerrar sesión
          </button>
        </div>
      </div>

      <div className="admin-tabs">
        {[
          ["style", "Estilo, Logos y Portada"],
          ["series", `Series (${draft.series.length})`],
          ["news", `Noticias (${(draft.news || []).length})`],
          ["projects", `Proyectos (${draft.projects.length})`],
          ["merch", `Merch (${draft.merch.length})`],
          ["pages", `Páginas nuevas (${draft.customPages.length})`],
          ["security", "Respaldo y Contraseña"],
        ].map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={`nav-link ${activeTab === id ? "is-active" : ""}`}
            onClick={() => setActiveTab(id)}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ---------------- TAB: ESTILO, LOGOS Y PORTADA ---------------- */}
      {activeTab === "style" && (
        <div className="admin-section">
          <div className="admin-item-card">
            <h3 className="proj-title">
              1. Logotipos e Identidad de Marca
            </h3>
            <div className="admin-form-grid">
              <label className="admin-field">
                <span>Nombre en Barra Superior (Principal)</span>
                <input
                  className="admin-input"
                  value={draft.settings.brandTitle || ""}
                  onChange={(e) => updateSettings({ brandTitle: e.target.value })}
                />
              </label>
              <label className="admin-field">
                <span>Subtítulo en Barra Superior</span>
                <input
                  className="admin-input"
                  value={draft.settings.brandSub || ""}
                  onChange={(e) => updateSettings({ brandSub: e.target.value })}
                />
              </label>
              <div className="admin-field" style={{ gridColumn: "1 / -1" }}>
                <span>Icono / Logo de la Barra Superior (deja vacío para usar la máscara TWSTD)</span>
                <div className="admin-inline-row">
                  <input
                    className="admin-input"
                    placeholder="URL de imagen del logo o sube un archivo →"
                    value={draft.settings.brandLogoUrl || ""}
                    onChange={(e) => updateSettings({ brandLogoUrl: e.target.value })}
                  />
                  <label className="btn btn-primary btn-sm" style={{ cursor: "pointer" }}>
                    ↑ Subir logo
                    <input
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={async (e) => {
                        const f = e.target.files && e.target.files[0];
                        if (!f) return;
                        const res = await handleUploadFile(f);
                        if (res && res.url) updateSettings({ brandLogoUrl: res.url });
                        e.target.value = "";
                      }}
                    />
                  </label>
                  {draft.settings.brandLogoUrl && (
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={() => updateSettings({ brandLogoUrl: "" })}
                    >
                      Usar máscara original
                    </button>
                  )}
                </div>
              </div>
              <div className="admin-field" style={{ gridColumn: "1 / -1" }}>
                <span>Logo Gráfico Grande en Inicio (opcional, reemplaza al texto "TWSTD PRODUCTIONS")</span>
                <div className="admin-inline-row">
                  <input
                    className="admin-input"
                    placeholder="Deja vacío para usar el título con efecto Glitch, o sube un logo PNG/SVG →"
                    value={draft.settings.heroLogoUrl || ""}
                    onChange={(e) => updateSettings({ heroLogoUrl: e.target.value })}
                  />
                  <label className="btn btn-primary btn-sm" style={{ cursor: "pointer" }}>
                    ↑ Subir logo de portada
                    <input
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={async (e) => {
                        const f = e.target.files && e.target.files[0];
                        if (!f) return;
                        const res = await handleUploadFile(f);
                        if (res && res.url) updateSettings({ heroLogoUrl: res.url });
                        e.target.value = "";
                      }}
                    />
                  </label>
                  {draft.settings.heroLogoUrl && (
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={() => updateSettings({ heroLogoUrl: "" })}
                    >
                      Quitar imagen
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="admin-item-card">
            <h3 className="proj-title">
              2. Portada Principal y Banner Rotativo (Derecha del Logo)
            </h3>
            <div className="admin-form-grid">
              <label className="admin-field">
                <span>Eyebrow superior</span>
                <input
                  className="admin-input"
                  value={draft.settings.heroEyebrow || ""}
                  onChange={(e) => updateSettings({ heroEyebrow: e.target.value })}
                />
              </label>
              <label className="admin-field">
                <span>Título principal en Inicio</span>
                <input
                  className="admin-input"
                  value={draft.settings.heroTitle || ""}
                  onChange={(e) => updateSettings({ heroTitle: e.target.value })}
                />
              </label>
              <label className="admin-field" style={{ gridColumn: "1 / -1" }}>
                <span>Descripción de bienvenida</span>
                <textarea
                  className="admin-input admin-textarea"
                  rows={2}
                  value={draft.settings.heroSub || ""}
                  onChange={(e) => updateSettings({ heroSub: e.target.value })}
                />
              </label>
            </div>

            <BannersEditor
              title="Ciclo de Imágenes del Banner de Inicio (lado derecho del logo)"
              banners={draft.settings.heroBanners || []}
              onChange={(heroBanners) => updateSettings({ heroBanners })}
              onUploadFile={handleUploadFile}
            />

            <LinksEditor
              links={draft.settings.socialLinks || []}
              onChange={(socialLinks) => updateSettings({ socialLinks })}
              placeholderLabel="YouTube Oficial"
            />
          </div>

          <div className="admin-item-card">
            <div className="admin-item-head">
              <h3 className="proj-title">
                3. Estilo y Colores Globales del Sitio
              </h3>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => updateSettings({ style: { ...DEFAULT_SITE_SETTINGS.style } })}
              >
                Restaurar paleta original TWSTD
              </button>
            </div>
            <div className="admin-form-grid">
              <ColorField
                label="Fondo principal del sitio (bg)"
                value={draft.settings.style.bg}
                onChange={(bg) => updateSiteStyle({ bg })}
              />
              <ColorField
                label="Fondo de tarjetas / paneles (panel)"
                value={draft.settings.style.panel}
                onChange={(panel) => updateSiteStyle({ panel })}
              />
              <ColorField
                label="Color Acento Cian (Glitch 1 / Destacados)"
                value={draft.settings.style.cyan}
                onChange={(cyan) => updateSiteStyle({ cyan })}
              />
              <ColorField
                label="Color Acento Magenta (Glitch 2 / Sombra)"
                value={draft.settings.style.magenta}
                onChange={(magenta) => updateSiteStyle({ magenta })}
              />
              <ColorField
                label="Color de texto principal (ink)"
                value={draft.settings.style.ink}
                onChange={(ink) => updateSiteStyle({ ink })}
              />
              <ColorField
                label="Color de texto secundario (muted)"
                value={draft.settings.style.muted}
                onChange={(muted) => updateSiteStyle({ muted })}
              />
              <div className="admin-field" style={{ gridColumn: "1 / -1" }}>
                <span>Imagen o textura de fondo global (opcional)</span>
                <div className="admin-inline-row">
                  <input
                    className="admin-input"
                    placeholder="Deja vacío para fondo negro puro, o sube una textura →"
                    value={draft.settings.style.bgImage || ""}
                    onChange={(e) => updateSiteStyle({ bgImage: e.target.value })}
                  />
                  <label className="btn btn-ghost btn-sm" style={{ cursor: "pointer" }}>
                    ↑ Subir fondo
                    <input
                      type="file"
                      accept="image/*"
                      style={{ display: "none" }}
                      onChange={async (e) => {
                        const f = e.target.files && e.target.files[0];
                        if (!f) return;
                        const res = await handleUploadFile(f);
                        if (res && res.url) updateSiteStyle({ bgImage: res.url });
                        e.target.value = "";
                      }}
                    />
                  </label>
                  {draft.settings.style.bgImage && (
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={() => updateSiteStyle({ bgImage: "" })}
                    >
                      Quitar
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---------------- TAB: SERIES ---------------- */}
      {activeTab === "series" && (
        <div className="admin-section">
          <div className="admin-section-bar">
            <p className="section-sub" style={{ margin: 0 }}>
              Cualquier cambio aquí (título, imágenes, logo o paleta de colores) se actualiza automáticamente tanto en la página de la serie como en su tarjeta de acceso directo en Inicio.
            </p>
            <button type="button" className="btn btn-primary" onClick={addSeries}>
              + Nueva Serie
            </button>
          </div>

          {draft.series.map((s, idx) => (
            <div key={idx} className="admin-item-card">
              <div className="admin-item-head">
                <h3 className="proj-title">
                  {idx + 1}. {s.title || "Sin título"}
                </h3>
                <div className="admin-btn-group">
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => go("seriesDetail", s.id)}
                  >
                    Ver serie ↗
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={() => removeSeries(idx)}
                  >
                    Eliminar
                  </button>
                </div>
              </div>

              <div className="admin-form-grid">
                <label className="admin-field">
                  <span>Título de la serie</span>
                  <input
                    className="admin-input"
                    value={s.title || ""}
                    onChange={(e) => updateSeries(idx, { title: e.target.value })}
                  />
                </label>
                <label className="admin-field">
                  <span>ID de URL (#/series/ID)</span>
                  <input
                    className="admin-input"
                    value={s.id || ""}
                    onChange={(e) => updateSeries(idx, { id: slugify(e.target.value) })}
                  />
                </label>
                <label className="admin-field">
                  <span>Etiqueta superior (Eyebrow)</span>
                  <input
                    className="admin-input"
                    value={s.eyebrow || ""}
                    onChange={(e) => updateSeries(idx, { eyebrow: e.target.value })}
                  />
                </label>
                <label className="admin-field">
                  <span>Metadatos (Episodios / Año / Formato)</span>
                  <input
                    className="admin-input"
                    value={s.meta || ""}
                    onChange={(e) => updateSeries(idx, { meta: e.target.value })}
                  />
                </label>
                <label className="admin-field" style={{ gridColumn: "1 / -1" }}>
                  <span>Frase gancho (Tagline en la tarjeta de Inicio y Series)</span>
                  <input
                    className="admin-input"
                    value={s.tagline || ""}
                    onChange={(e) => updateSeries(idx, { tagline: e.target.value })}
                  />
                </label>
                <div className="admin-field" style={{ gridColumn: "1 / -1" }}>
                  <span>Logo gráfico de la serie (opcional)</span>
                  <div className="admin-inline-row">
                    <input
                      className="admin-input"
                      placeholder="URL del logo de la serie o sube un PNG transparente →"
                      value={s.logoUrl || ""}
                      onChange={(e) => updateSeries(idx, { logoUrl: e.target.value })}
                    />
                    <label className="btn btn-ghost btn-sm" style={{ cursor: "pointer" }}>
                      ↑ Subir logo
                      <input
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={async (e) => {
                          const f = e.target.files && e.target.files[0];
                          if (!f) return;
                          const res = await handleUploadFile(f);
                          if (res && res.url) updateSeries(idx, { logoUrl: res.url });
                          e.target.value = "";
                        }}
                      />
                    </label>
                    {s.logoUrl && (
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => updateSeries(idx, { logoUrl: "" })}
                      >
                        Quitar
                      </button>
                    )}
                  </div>
                </div>
                <label className="admin-field" style={{ gridColumn: "1 / -1" }}>
                  <span>Sinopsis completa</span>
                  <textarea
                    className="admin-input admin-textarea"
                    rows={3}
                    value={s.synopsis || ""}
                    onChange={(e) => updateSeries(idx, { synopsis: e.target.value })}
                  />
                </label>
                <label className="admin-checkbox">
                  <input
                    type="checkbox"
                    checked={Boolean(s.isPlaceholder)}
                    onChange={(e) => updateSeries(idx, { isPlaceholder: e.target.checked })}
                  />
                  <span>Mostrar insignia "En producción"</span>
                </label>
              </div>

              <SeriesPaletteEditor
                palette={s.palette}
                onChange={(palette) => updateSeries(idx, { palette })}
              />

              <LinksEditor
                links={s.links || []}
                onChange={(links) => updateSeries(idx, { links })}
                placeholderLabel="Ver en YouTube"
              />

              <BannersEditor
                title="Imágenes de la Serie (1.ª imagen = Miniatura de los botones en Inicio y #/series; 2.ª en adelante = Banners y galería dentro de la serie)"
                banners={s.banners || []}
                onChange={(banners) => updateSeries(idx, { banners })}
                onUploadFile={handleUploadFile}
              />

              <FilesEditor
                files={s.files || []}
                onChange={(files) => updateSeries(idx, { files })}
                onUploadFile={handleUploadFile}
              />

              <div className="admin-subbox">
                <div className="admin-subbox-head">
                  <span className="eyebrow" style={{ marginBottom: 0 }}>
                    Merch asociada a esta serie ({draft.merch.filter((m) => m.seriesId === s.id).length})
                  </span>
                  <div className="admin-btn-group">
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={() => {
                        addMerch(s.id);
                        setActiveTab("merch");
                      }}
                    >
                      + Crear artículo para {s.title}
                    </button>
                    <button
                      type="button"
                      className="btn btn-ghost btn-sm"
                      onClick={() => setActiveTab("merch")}
                    >
                      Ir a pestaña Merch →
                    </button>
                  </div>
                </div>
                {draft.merch.filter((m) => m.seriesId === s.id).length === 0 ? (
                  <p className="admin-hint">
                    No hay artículos asignados a esta serie todavía. Puedes asignarlos desde la pestaña Merch o con el botón "+ Crear artículo".
                  </p>
                ) : (
                  <div className="admin-inline-row">
                    {draft.merch
                      .filter((m) => m.seriesId === s.id)
                      .map((m) => (
                        <span key={m.id} className="status status-dev">
                          {m.title} ({m.price})
                        </span>
                      ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ---------------- TAB: NOTICIAS ---------------- */}
      {activeTab === "news" && (
        <div className="admin-section">
          <div className="admin-section-bar">
            <p className="section-sub" style={{ margin: 0 }}>
              Personaliza la barra de Noticias que aparece en la página de Inicio. Puedes añadir comunicados, avances, imágenes, archivos y botones externos.
            </p>
            <button type="button" className="btn btn-primary" onClick={addNews}>
              + Nueva Noticia
            </button>
          </div>

          {(draft.news || []).map((n, idx) => (
            <div key={idx} className="admin-item-card">
              <div className="admin-item-head">
                <h3 className="proj-title">
                  {idx + 1}. {n.title || "Sin título"}
                </h3>
                <div className="admin-btn-group">
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => go("newsDetail", n.id)}
                  >
                    Ver noticia ↗
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={() => removeNews(idx)}
                  >
                    Eliminar
                  </button>
                </div>
              </div>

              <div className="admin-form-grid">
                <label className="admin-field">
                  <span>Título de la noticia</span>
                  <input
                    className="admin-input"
                    value={n.title || ""}
                    onChange={(e) => updateNews(idx, { title: e.target.value })}
                  />
                </label>
                <label className="admin-field">
                  <span>ID de URL (#/news/ID)</span>
                  <input
                    className="admin-input"
                    value={n.id || ""}
                    onChange={(e) => updateNews(idx, { id: slugify(e.target.value) })}
                  />
                </label>
                <label className="admin-field">
                  <span>Categoría / Etiqueta (Eyebrow)</span>
                  <input
                    className="admin-input"
                    placeholder="ej. Anuncio · Estudio"
                    value={n.eyebrow || ""}
                    onChange={(e) => updateNews(idx, { eyebrow: e.target.value })}
                  />
                </label>
                <label className="admin-field">
                  <span>Fecha</span>
                  <input
                    className="admin-input"
                    placeholder="ej. Octubre 2026"
                    value={n.date || ""}
                    onChange={(e) => updateNews(idx, { date: e.target.value })}
                  />
                </label>
                <label className="admin-field" style={{ gridColumn: "1 / -1" }}>
                  <span>Resumen corto (en la tarjeta de la barra de Noticias en Inicio)</span>
                  <textarea
                    className="admin-input admin-textarea"
                    rows={2}
                    value={n.desc || ""}
                    onChange={(e) => updateNews(idx, { desc: e.target.value })}
                  />
                </label>
                <label className="admin-field" style={{ gridColumn: "1 / -1" }}>
                  <span>Contenido completo de la noticia</span>
                  <textarea
                    className="admin-input admin-textarea"
                    rows={4}
                    value={n.synopsis || ""}
                    onChange={(e) => updateNews(idx, { synopsis: e.target.value })}
                  />
                </label>
              </div>

              <LinksEditor
                links={n.links || []}
                onChange={(links) => updateNews(idx, { links })}
                placeholderLabel="Leer más / Ver video"
              />

              <BannersEditor
                title="Imágenes de la Noticia (la 1.ª imagen es la portada en la barra de Noticias de Inicio)"
                banners={n.banners || []}
                onChange={(banners) => updateNews(idx, { banners })}
                onUploadFile={handleUploadFile}
              />

              <FilesEditor
                files={n.files || []}
                onChange={(files) => updateNews(idx, { files })}
                onUploadFile={handleUploadFile}
              />
            </div>
          ))}
        </div>
      )}

      {/* ---------------- TAB: PROYECTOS ---------------- */}
      {activeTab === "projects" && (
        <div className="admin-section">
          <div className="admin-section-bar">
            <p className="section-sub" style={{ margin: 0 }}>
              Edita cada proyecto, sube imágenes o archivos adjuntos y añade botones como "Ver en YouTube" o "Jugar en Steam". Todos los proyectos aparecen con animación Glitch en Inicio.
            </p>
            <button type="button" className="btn btn-primary" onClick={addProject}>
              + Nuevo Proyecto
            </button>
          </div>

          {draft.projects.map((p, idx) => (
            <div key={idx} className="admin-item-card">
              <div className="admin-item-head">
                <h3 className="proj-title">
                  {idx + 1}. {p.title || "Sin título"}
                </h3>
                <div className="admin-btn-group">
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => go("projectDetail", p.id)}
                  >
                    Ver proyecto ↗
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={() => removeProject(idx)}
                  >
                    Eliminar
                  </button>
                </div>
              </div>

              <div className="admin-form-grid">
                <label className="admin-field">
                  <span>Título del proyecto</span>
                  <input
                    className="admin-input"
                    value={p.title || ""}
                    onChange={(e) => updateProject(idx, { title: e.target.value })}
                  />
                </label>
                <label className="admin-field">
                  <span>ID de URL (#/projects/ID)</span>
                  <input
                    className="admin-input"
                    value={p.id || ""}
                    onChange={(e) => updateProject(idx, { id: slugify(e.target.value) })}
                  />
                </label>
                <label className="admin-field">
                  <span>Tipo / Formato</span>
                  <input
                    className="admin-input"
                    placeholder="ej. Videojuego · survival horror"
                    value={p.type || ""}
                    onChange={(e) => updateProject(idx, { type: e.target.value })}
                  />
                </label>
                <label className="admin-field">
                  <span>Estado</span>
                  <input
                    className="admin-input"
                    placeholder="ej. En desarrollo, Activo, Pausado"
                    value={p.status || ""}
                    onChange={(e) => updateProject(idx, { status: e.target.value })}
                  />
                </label>
                <label className="admin-field" style={{ gridColumn: "1 / -1" }}>
                  <span>Motor / Plataformas</span>
                  <input
                    className="admin-input"
                    placeholder="ej. Unreal Engine 5 · YouTube · Steam"
                    value={p.engine || ""}
                    onChange={(e) => updateProject(idx, { engine: e.target.value })}
                  />
                </label>
                <label className="admin-field" style={{ gridColumn: "1 / -1" }}>
                  <span>Descripción corta (en Inicio y lista de proyectos)</span>
                  <textarea
                    className="admin-input admin-textarea"
                    rows={2}
                    value={p.desc || ""}
                    onChange={(e) => updateProject(idx, { desc: e.target.value })}
                  />
                </label>
                <label className="admin-field" style={{ gridColumn: "1 / -1" }}>
                  <span>Descripción completa / Sinopsis (en la página individual del proyecto)</span>
                  <textarea
                    className="admin-input admin-textarea"
                    rows={4}
                    value={p.synopsis || ""}
                    onChange={(e) => updateProject(idx, { synopsis: e.target.value })}
                  />
                </label>
              </div>

              <LinksEditor
                links={p.links || []}
                onChange={(links) => updateProject(idx, { links })}
                placeholderLabel="Ver en YouTube"
              />

              <BannersEditor
                banners={p.banners || []}
                onChange={(banners) => updateProject(idx, { banners })}
                onUploadFile={handleUploadFile}
              />

              <FilesEditor
                files={p.files || []}
                onChange={(files) => updateProject(idx, { files })}
                onUploadFile={handleUploadFile}
              />
            </div>
          ))}
        </div>
      )}

      {/* ---------------- TAB: MERCH ---------------- */}
      {activeTab === "merch" && (
        <div className="admin-section">
          <div className="admin-item-card">
            <h3 className="proj-title" style={{ marginBottom: 14 }}>
              Conectar Tienda Externa Principal (Shopify, Fourthwall, MercadoLibre, etc.)
            </h3>
            <div className="admin-form-grid">
              <label className="admin-field">
                <span>Texto del botón de tienda general</span>
                <input
                  className="admin-input"
                  placeholder="ej. Visitar tienda oficial en Fourthwall"
                  value={draft.settings.merchStoreLabel || ""}
                  onChange={(e) => updateSettings({ merchStoreLabel: e.target.value })}
                />
              </label>
              <label className="admin-field">
                <span>URL de la tienda general</span>
                <input
                  className="admin-input"
                  placeholder="https://..."
                  value={draft.settings.merchStoreUrl || ""}
                  onChange={(e) => updateSettings({ merchStoreUrl: e.target.value })}
                />
              </label>
            </div>
          </div>

          <div className="admin-section-bar">
            <p className="section-sub" style={{ margin: 0 }}>
              Artículos individuales de Merch. Elige a qué serie pertenece cada artículo (o déjalo como Merch general del estudio) para que aparezca tanto en la sección de esa serie en #/merch como dentro de la página de la serie.
            </p>
            <button type="button" className="btn btn-primary" onClick={() => addMerch("")}>
              + Nuevo Artículo
            </button>
          </div>

          <div className="admin-merch-list">
            {draft.merch.map((m, idx) => (
              <div key={idx} className="admin-item-card">
                <div className="admin-item-head">
                  <h3 className="proj-title">
                    {idx + 1}. {m.title || "Artículo"}
                  </h3>
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={() => removeMerch(idx)}
                  >
                    Eliminar
                  </button>
                </div>
                <div className="admin-form-grid">
                  <label className="admin-field">
                    <span>Nombre del artículo</span>
                    <input
                      className="admin-input"
                      value={m.title || ""}
                      onChange={(e) => updateMerch(idx, { title: e.target.value })}
                    />
                  </label>
                  <label className="admin-field">
                    <span>Serie a la que pertenece</span>
                    <select
                      className="admin-input"
                      value={m.seriesId || ""}
                      onChange={(e) => {
                        const nextSeriesId = e.target.value;
                        const matched = draft.series.find((s) => s.id === nextSeriesId);
                        updateMerch(idx, {
                          seriesId: nextSeriesId,
                          tag: matched ? `Serie · ${matched.title}` : m.tag || "Estudio",
                        });
                      }}
                    >
                      <option value="">Estudio TWSTD (General / Sin serie específica)</option>
                      {draft.series.map((s) => (
                        <option key={s.id} value={s.id}>
                          Serie: {s.title}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="admin-field">
                    <span>Categoría / Etiqueta</span>
                    <input
                      className="admin-input"
                      placeholder="Estudio / Edición serie"
                      value={m.tag || ""}
                      onChange={(e) => updateMerch(idx, { tag: e.target.value })}
                    />
                  </label>
                  <label className="admin-field">
                    <span>Precio</span>
                    <input
                      className="admin-input"
                      placeholder="$350 MXN"
                      value={m.price || ""}
                      onChange={(e) => updateMerch(idx, { price: e.target.value })}
                    />
                  </label>
                  <label className="admin-field">
                    <span>Estado (cuando no hay link de compra)</span>
                    <input
                      className="admin-input"
                      placeholder="Próximamente / Agotado"
                      value={m.status || ""}
                      onChange={(e) => updateMerch(idx, { status: e.target.value })}
                    />
                  </label>
                  <label className="admin-field">
                    <span>URL de compra externa (opcional)</span>
                    <input
                      className="admin-input"
                      placeholder="https://tu-tienda.com/producto"
                      value={m.url || ""}
                      onChange={(e) => updateMerch(idx, { url: e.target.value })}
                    />
                  </label>
                  <label className="admin-field">
                    <span>Texto del botón de compra</span>
                    <input
                      className="admin-input"
                      placeholder="Comprar"
                      value={m.buttonLabel || ""}
                      onChange={(e) => updateMerch(idx, { buttonLabel: e.target.value })}
                    />
                  </label>
                  <div className="admin-field" style={{ gridColumn: "1 / -1" }}>
                    <span>Imagen del producto (deja vacío para usar el icono TWSTD)</span>
                    <div className="admin-inline-row">
                      <input
                        className="admin-input"
                        placeholder="URL de imagen o sube una foto →"
                        value={m.image || ""}
                        onChange={(e) => updateMerch(idx, { image: e.target.value })}
                      />
                      <label className="btn btn-ghost btn-sm" style={{ cursor: "pointer" }}>
                        ↑ Subir foto
                        <input
                          type="file"
                          accept="image/*"
                          style={{ display: "none" }}
                          onChange={async (e) => {
                            const f = e.target.files && e.target.files[0];
                            if (!f) return;
                            const res = await handleUploadFile(f);
                            if (res && res.url) updateMerch(idx, { image: res.url });
                            e.target.value = "";
                          }}
                        />
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ---------------- TAB: PÁGINAS NUEVAS ---------------- */}
      {activeTab === "pages" && (
        <div className="admin-section">
          <div className="admin-section-bar">
            <p className="section-sub" style={{ margin: 0 }}>
              Crea páginas nuevas para el sitio (ej. Prensa, Casting, Sobre Nosotros, Eventos) con su propio link en el menú superior, galerías, archivos y sitios conectados.
            </p>
            <button type="button" className="btn btn-primary" onClick={addCustomPage}>
              + Crear Nueva Página
            </button>
          </div>

          {draft.customPages.length === 0 && (
            <div className="admin-item-card">
              <p className="admin-hint">
                No has creado páginas adicionales todavía. Haz clic en "+ Crear Nueva Página" para añadir una.
              </p>
            </div>
          )}

          {draft.customPages.map((cp, idx) => (
            <div key={idx} className="admin-item-card">
              <div className="admin-item-head">
                <h3 className="proj-title">
                  {idx + 1}. {cp.title || "Nueva Página"} (#/p/{cp.id})
                </h3>
                <div className="admin-btn-group">
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    onClick={() => go("customPage", cp.id)}
                  >
                    Ver página ↗
                  </button>
                  <button
                    type="button"
                    className="btn btn-danger btn-sm"
                    onClick={() => removeCustomPage(idx)}
                  >
                    Eliminar
                  </button>
                </div>
              </div>

              <div className="admin-form-grid">
                <label className="admin-field">
                  <span>Título de la página</span>
                  <input
                    className="admin-input"
                    value={cp.title || ""}
                    onChange={(e) => updateCustomPage(idx, { title: e.target.value })}
                  />
                </label>
                <label className="admin-field">
                  <span>Slug de URL (#/p/SLUG)</span>
                  <input
                    className="admin-input"
                    value={cp.id || ""}
                    onChange={(e) => updateCustomPage(idx, { id: slugify(e.target.value) })}
                  />
                </label>
                <label className="admin-field">
                  <span>Nombre corto en barra de navegación</span>
                  <input
                    className="admin-input"
                    value={cp.navLabel || ""}
                    onChange={(e) => updateCustomPage(idx, { navLabel: e.target.value })}
                  />
                </label>
                <label className="admin-field">
                  <span>Subtítulo / Metadatos</span>
                  <input
                    className="admin-input"
                    value={cp.subtitle || ""}
                    onChange={(e) => updateCustomPage(idx, { subtitle: e.target.value })}
                  />
                </label>
                <label className="admin-checkbox" style={{ gridColumn: "1 / -1" }}>
                  <input
                    type="checkbox"
                    checked={Boolean(cp.showInNav)}
                    onChange={(e) => updateCustomPage(idx, { showInNav: e.target.checked })}
                  />
                  <span>Mostrar esta página en el menú superior del sitio</span>
                </label>
                <label className="admin-field" style={{ gridColumn: "1 / -1" }}>
                  <span>Contenido / Descripción de la página</span>
                  <textarea
                    className="admin-input admin-textarea"
                    rows={4}
                    value={cp.body || ""}
                    onChange={(e) => updateCustomPage(idx, { body: e.target.value })}
                  />
                </label>
              </div>

              <LinksEditor
                links={cp.links || []}
                onChange={(links) => updateCustomPage(idx, { links })}
                placeholderLabel="Abrir enlace"
              />

              <BannersEditor
                banners={cp.banners || []}
                onChange={(banners) => updateCustomPage(idx, { banners })}
                onUploadFile={handleUploadFile}
              />

              <FilesEditor
                files={cp.files || []}
                onChange={(files) => updateCustomPage(idx, { files })}
                onUploadFile={handleUploadFile}
              />
            </div>
          ))}
        </div>
      )}

      {/* ---------------- TAB: RESPALDO Y CONTRASEÑA ---------------- */}
      {activeTab === "security" && (
        <div className="admin-section">
          <div className="admin-item-card">
            <h3 className="proj-title" style={{ marginBottom: 14 }}>
              Cambiar Contraseña de Administrador
            </h3>
            <form onSubmit={handleChangePassword} className="admin-form-grid">
              <label className="admin-field">
                <span>Contraseña actual</span>
                <input
                  type="password"
                  className="admin-input"
                  value={currPass}
                  onChange={(e) => setCurrPass(e.target.value)}
                />
              </label>
              <label className="admin-field">
                <span>Nueva contraseña</span>
                <input
                  type="password"
                  className="admin-input"
                  value={newPass}
                  onChange={(e) => setNewPass(e.target.value)}
                />
              </label>
              <div style={{ gridColumn: "1 / -1", display: "flex", gap: 12, alignItems: "center" }}>
                <button type="submit" className="btn btn-primary btn-sm">
                  Actualizar contraseña
                </button>
                {passMsg && <span className="admin-save-notice">{passMsg}</span>}
              </div>
            </form>
          </div>

          <div className="admin-item-card">
            <h3 className="proj-title" style={{ marginBottom: 14 }}>
              Respaldo y Restauración (JSON)
            </h3>
            <p className="admin-hint" style={{ marginBottom: 16 }}>
              Puedes descargar una copia de seguridad de todo el contenido y estilos del sitio o importar un archivo JSON.
            </p>
            <div className="hero-actions">
              <button type="button" className="btn btn-ghost btn-sm" onClick={exportJson}>
                ↓ Exportar contenido (.json)
              </button>
              <label className="btn btn-ghost btn-sm" style={{ cursor: "pointer" }}>
                ↑ Importar contenido (.json)
                <input
                  type="file"
                  accept="application/json"
                  style={{ display: "none" }}
                  onChange={importJson}
                />
              </label>
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={() => {
                  if (window.confirm("¿Restaurar todo el contenido y estilos originales por defecto?")) {
                    const def = getDefaultContent();
                    applyDraft(def);
                    saveChanges(def);
                  }
                }}
              >
                Restablecer valores por defecto
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------------------------- Footer ---------------------------------- */

function Footer({ settings }) {
  return (
    <footer className="footer">
      <div className="footer-row">
        <span>
          {(settings && settings.brandTitle) || "TWSTD"}{" "}
          {(settings && settings.brandSub) || "PRODUCTIONS"} © {new Date().getFullYear()}
        </span>
        <span className="footer-sub">Series · Proyectos · Merch</span>
      </div>
    </footer>
  );
}

/* ----------------------------------- App ----------------------------------- */

export default function App() {
  const [route, setRoute] = useState(() => {
    if (typeof window === "undefined") return { page: "home", itemId: null };
    return parseHashRoute(window.location.hash);
  });

  const [content, setContent] = useState(() => {
    if (typeof window !== "undefined") {
      try {
        const cached = localStorage.getItem(STORAGE_KEY);
        if (cached) return normalizeContent(JSON.parse(cached));
      } catch {}
    }
    return getDefaultContent();
  });

  const [serverAvailable, setServerAvailable] = useState(false);

  // History & Hash router with a back-button guard so pressing Back never leaves the site
  useEffect(() => {
    if (typeof window === "undefined") return;

    const initialHash = window.location.hash || "#/";
    const initialRoute = parseHashRoute(initialHash);

    // Push a guard state at the bottom of the history stack so pressing Back at Home stays inside the site
    window.history.replaceState({ twstdGuard: true, page: "home", itemId: null }, "", "#/");
    window.history.pushState(
      { twstdApp: true, page: initialRoute.page, itemId: initialRoute.itemId },
      "",
      initialHash
    );
    setRoute(initialRoute);

    const syncFromLocation = (e) => {
      if (e && e.state && e.state.twstdGuard) {
        // User pressed Back from the root view; keep them on Home inside the site
        window.history.pushState({ twstdApp: true, page: "home", itemId: null }, "", "#/");
        setRoute({ page: "home", itemId: null });
        window.scrollTo({ top: 0, behavior: "auto" });
        return;
      }
      const parsed = parseHashRoute(window.location.hash);
      setRoute(parsed);
      window.scrollTo({ top: 0, behavior: "auto" });
    };

    const onKeyDown = (e) => {
      if (e.ctrlKey && e.shiftKey && (e.key === "A" || e.key === "a")) {
        e.preventDefault();
        const adminHash = "#/admin";
        window.history.pushState({ twstdApp: true, page: "admin", itemId: null }, "", adminHash);
        setRoute({ page: "admin", itemId: null });
        window.scrollTo({ top: 0, behavior: "auto" });
      }
    };

    window.addEventListener("popstate", syncFromLocation);
    window.addEventListener("hashchange", syncFromLocation);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("popstate", syncFromLocation);
      window.removeEventListener("hashchange", syncFromLocation);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  // Load live content from cPanel api.php if available
  useEffect(() => {
    let active = true;
    fetch("api.php?action=get_content", { cache: "no-store" })
      .then((res) => {
        const ct = res.headers.get("content-type") || "";
        if (!ct.includes("application/json")) throw new Error("Not JSON");
        return res.json();
      })
      .then((data) => {
        if (!active) return;
        if (data && data.ok) {
          setServerAvailable(true);
          if (data.content) {
            const normalized = normalizeContent(data.content);
            setContent(normalized);
            try {
              localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
            } catch {}
          }
        }
      })
      .catch(() => {
        // Running locally without PHP server; uses localStorage / defaults
      });
    return () => {
      active = false;
    };
  }, []);

  const go = useCallback((nextPage, id = null) => {
    const targetHash = buildHashUrl(nextPage, id);
    if (typeof window !== "undefined") {
      if (window.location.hash !== targetHash) {
        window.history.pushState({ twstdApp: true, page: nextPage, itemId: id }, "", targetHash);
      }
      setRoute({ page: nextPage, itemId: id });
      window.scrollTo({ top: 0, behavior: "auto" });
    } else {
      setRoute({ page: nextPage, itemId: id });
    }
  }, []);

  const handleLiveChange = useCallback((updatedContent) => {
    setContent(updatedContent);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedContent));
    } catch {}
  }, []);

  async function handleSaveContent(newContent, token) {
    const normalized = normalizeContent(newContent);
    setContent(normalized);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
    } catch {}

    if (serverAvailable && token && token !== "local-session") {
      try {
        const resp = await fetch("api.php?action=save_content", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Admin-Token": token,
          },
          body: JSON.stringify({ token, content: normalized }),
        });
        const data = await resp.json();
        if (data && data.ok) {
          return { ok: true, mode: "server" };
        }
        return { ok: false, error: data.error || "Error al guardar en el servidor" };
      } catch {
        return { ok: false, error: "No se pudo conectar con api.php" };
      }
    }
    return { ok: true, mode: "local" };
  }

  const activeStyle = {
    ...TOKENS,
    ...((content.settings && content.settings.style) || {}),
  };

  let view;
  if (route.page === "series") {
    view = <SeriesList go={go} series={content.series} />;
  } else if (route.page === "seriesDetail") {
    view = (
      <SeriesDetail
        id={route.itemId}
        go={go}
        series={content.series}
        merch={content.merch}
        settings={content.settings}
      />
    );
  } else if (route.page === "projects") {
    view = <Projects go={go} projects={content.projects} />;
  } else if (route.page === "projectDetail") {
    view = <ProjectDetail id={route.itemId} go={go} projects={content.projects} />;
  } else if (route.page === "news") {
    view = <NewsList go={go} news={content.news} />;
  } else if (route.page === "newsDetail") {
    view = <NewsDetail id={route.itemId} go={go} news={content.news} />;
  } else if (route.page === "merch") {
    view = (
      <Merch
        merch={content.merch}
        series={content.series}
        settings={content.settings}
        go={go}
      />
    );
  } else if (route.page === "customPage") {
    view = <CustomPageView slug={route.itemId} go={go} customPages={content.customPages} />;
  } else if (route.page === "admin") {
    view = (
      <AdminDashboard
        content={content}
        onLiveChange={handleLiveChange}
        onSaveContent={handleSaveContent}
        serverAvailable={serverAvailable}
        go={go}
      />
    );
  } else {
    view = <Home go={go} content={content} />;
  }

  return (
    <div
      className="app"
      style={
        activeStyle.bgImage
          ? {
              backgroundImage: `linear-gradient(rgba(10,10,12,0.88), rgba(10,10,12,0.94)), ${formatBannerBg(
                activeStyle.bgImage
              )}`,
              backgroundAttachment: "fixed",
            }
          : undefined
      }
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Anton&family=Space+Mono:wght@400;700&family=Inter:wght@400;500;600&display=swap');

        :root {
          --bg: ${activeStyle.bg};
          --panel: ${activeStyle.panel};
          --panel2: ${activeStyle.panel2};
          --ink: ${activeStyle.ink};
          --muted: ${activeStyle.muted};
          --cyan: ${activeStyle.cyan};
          --magenta: ${activeStyle.magenta};
          --line: ${TOKENS.line};
          --s-accent1: ${activeStyle.cyan};
          --s-accent2: ${activeStyle.magenta};
          --s-card-bg: ${activeStyle.panel};
          --s-text: ${activeStyle.ink};
        }

        * { box-sizing: border-box; }
        .app {
          background-color: var(--bg);
          color: var(--ink);
          font-family: 'Inter', system-ui, sans-serif;
          min-height: 100vh;
          width: 100%;
          display: flex;
          flex-direction: column;
        }
        button { font-family: inherit; cursor: pointer; }
        a { text-decoration: none; color: inherit; }

        .eyebrow {
          font-family: 'Space Mono', monospace;
          font-size: 11px;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: var(--muted);
          margin-bottom: 8px;
        }

        /* -------- nav -------- */
        .nav {
          position: sticky; top: 0; z-index: 40;
          display: flex; align-items: center; justify-content: space-between;
          padding: 16px 28px;
          background: rgba(10,10,12,0.85);
          backdrop-filter: blur(10px);
          border-bottom: 1px solid var(--line);
          gap: 12px;
          flex-wrap: wrap;
        }
        .brand { display: flex; align-items: center; gap: 10px; background: none; border: none; padding: 0; color: inherit; }
        .brand-word {
          font-family: 'Anton', sans-serif;
          font-size: 18px;
          letter-spacing: 0.04em;
          line-height: 1;
          display: flex; flex-direction: column; align-items: flex-start;
        }
        .brand-word-sub { font-family: 'Space Mono', monospace; font-size: 8px; letter-spacing: 0.2em; color: var(--muted); margin-top: 2px; }
        .nav-links { display: flex; gap: 4px; flex-wrap: wrap; }
        .nav-link {
          background: none; border: none; color: var(--muted);
          font-family: 'Space Mono', monospace; font-size: 11px; letter-spacing: 0.1em;
          padding: 8px 12px; border-radius: 4px; transition: color .2s ease, background .2s ease;
        }
        .nav-link:hover { color: var(--ink); background: var(--panel); }
        .nav-link.is-active { color: var(--bg); background: var(--ink); }
        .nav-link:focus-visible, .brand:focus-visible, .btn:focus-visible, .card:focus-visible, .proj-card:focus-visible, .merch-card:focus-visible, .admin-input:focus-visible {
          outline: 2px solid var(--cyan); outline-offset: 2px;
        }

        /* -------- page fade -------- */
        .page { flex: 1; width: 100%; animation: fadeIn 0.55s ease both; padding: 0 28px 80px; max-width: 1180px; margin: 0 auto; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
        @media (prefers-reduced-motion: reduce) { .page { animation: none; } }

        /* -------- hero split + right-side cycling banner -------- */
        .hero { padding: 72px 0 56px; }
        .hero-split {
          display: grid;
          grid-template-columns: minmax(0, 1.08fr) minmax(300px, 0.92fr);
          gap: 36px;
          align-items: center;
        }
        .hero-left { max-width: 640px; }
        .hero-title { font-family: 'Anton', sans-serif; font-size: clamp(40px, 6.2vw, 74px); line-height: 0.98; margin: 6px 0 20px; letter-spacing: 0.01em; }
        .hero-custom-logo-wrap { margin: 8px 0 20px; }
        .hero-custom-logo { max-width: 100%; max-height: 130px; object-fit: contain; display: block; }
        .hero-sub { font-size: 17px; line-height: 1.6; color: var(--muted); max-width: 540px; margin-bottom: 32px; }
        .hero-actions { display: flex; gap: 12px; flex-wrap: wrap; align-items: center; }

        .hero-right { width: 100%; }
        .hero-showcase {
          position: relative;
          height: 340px;
          border-radius: 10px;
          overflow: hidden;
          background: var(--panel);
          border: 1px solid var(--line);
          box-shadow: -3px -3px 0px rgba(139, 224, 255, 0.35), 3px 3px 0px rgba(128, 0, 51, 0.55);
        }
        .hero-showcase-layer {
          position: absolute;
          inset: 0;
          transition: opacity 1.1s ease, transform 5s ease;
        }
        .hero-showcase-scanlines {
          position: absolute;
          inset: 0;
          background: repeating-linear-gradient(
            to bottom,
            transparent 0px,
            transparent 2px,
            rgba(10, 10, 12, 0.22) 3px
          );
          pointer-events: none;
        }
        .hero-showcase-scrim {
          position: absolute;
          inset: 0;
          background: linear-gradient(180deg, rgba(10,10,12,0.08) 0%, rgba(10,10,12,0.55) 100%);
          pointer-events: none;
        }
        .hero-showcase-dots {
          position: absolute;
          right: 16px;
          bottom: 14px;
          display: flex;
          gap: 6px;
          z-index: 2;
        }

        /* -------- buttons + generic glitch animation -------- */
        .btn {
          position: relative;
          display: inline-flex; align-items: center; justify-content: center;
          border-radius: 4px; padding: 12px 22px; font-size: 13px;
          font-family: 'Space Mono', monospace; letter-spacing: 0.06em;
          border: 1px solid transparent; transition: background .2s ease, color .2s ease, border-color .2s ease, transform .15s ease;
        }
        .btn-sm { padding: 7px 12px; font-size: 11px; }
        .btn-primary { background: var(--ink); color: var(--bg); }
        .btn-primary:hover { background: var(--s-accent1, var(--cyan)); color: var(--bg); }
        .btn-ghost { background: transparent; color: var(--ink); border-color: var(--line); }
        .btn-ghost:hover { border-color: var(--s-accent1, var(--cyan)); }
        .btn-danger { background: rgba(128,0,51,0.25); color: #ff80ab; border-color: rgba(128,0,51,0.6); }
        .btn-danger:hover { background: var(--magenta); color: var(--ink); }

        /* Little glitch animation on main homepage buttons */
        .btn-glitch {
          overflow: hidden;
          animation: btnGlitchIdle 5.5s infinite;
        }
        .btn-glitch::before,
        .btn-glitch::after {
          content: attr(data-text);
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          pointer-events: none;
          opacity: 0;
        }
        .btn-glitch::before {
          color: var(--s-accent1, var(--cyan));
          animation: btnGlitchSliceA 5.5s infinite;
        }
        .btn-glitch::after {
          color: var(--s-accent2, var(--magenta));
          animation: btnGlitchSliceB 5.5s infinite;
        }
        .btn-glitch:hover {
          box-shadow: -2px 0 0 var(--s-accent1, var(--cyan)), 2px 0 0 var(--s-accent2, var(--magenta));
          transform: translate(-1px, 0);
        }
        .btn-glitch-inline {
          position: relative;
          display: inline-block;
          animation: btnGlitchIdle 6s infinite;
        }
        .btn-glitch-inline:hover {
          text-shadow: -2px 0 var(--cyan), 2px 0 var(--magenta);
        }

        @keyframes btnGlitchIdle {
          0%, 91%, 100% { transform: translate(0, 0); box-shadow: none; }
          92% { transform: translate(-2px, 1px); box-shadow: -2px 0 0 var(--s-accent1, var(--cyan)), 2px 0 0 var(--s-accent2, var(--magenta)); }
          93% { transform: translate(2px, -1px); box-shadow: 2px 0 0 var(--s-accent1, var(--cyan)), -2px 0 0 var(--s-accent2, var(--magenta)); }
          94% { transform: translate(-1px, 0); box-shadow: none; }
        }
        @keyframes btnGlitchSliceA {
          0%, 91%, 95%, 100% { opacity: 0; transform: translate(0, 0); clip-path: inset(0 0 0 0); }
          92% { opacity: 0.85; transform: translate(-4px, 0); clip-path: inset(15% 0 55% 0); }
          93% { opacity: 0.85; transform: translate(3px, -1px); clip-path: inset(60% 0 10% 0); }
        }
        @keyframes btnGlitchSliceB {
          0%, 91%, 95%, 100% { opacity: 0; transform: translate(0, 0); clip-path: inset(0 0 0 0); }
          92% { opacity: 0.85; transform: translate(4px, 1px); clip-path: inset(50% 0 20% 0); }
          93% { opacity: 0.85; transform: translate(-3px, 0); clip-path: inset(10% 0 65% 0); }
        }

        .ext-links { display: flex; gap: 10px; flex-wrap: wrap; align-items: center; }

        /* -------- glitch text -------- */
        .glitch { position: relative; display: inline-block; }
        .glitch::before, .glitch::after {
          content: attr(data-text); position: absolute; left: 0; top: 0; width: 100%;
          opacity: 0.7; clip-path: inset(0 0 0 0);
          pointer-events: none;
        }
        .glitch::before { color: var(--s-accent1, var(--cyan)); transform: translate(-2px, 0); animation: glitchA 7s infinite; }
        .glitch::after { color: var(--s-accent2, var(--magenta)); transform: translate(2px, 0); animation: glitchB 7s infinite; }

        /* Subtle glitch for project & series card titles */
        .glitch-subtle { position: relative; display: inline-block; }
        .glitch-subtle::before, .glitch-subtle::after {
          content: attr(data-text);
          position: absolute; left: 0; top: 0; width: 100%;
          pointer-events: none;
          opacity: 0;
        }
        .glitch-subtle::before {
          color: var(--s-accent1, var(--cyan));
          animation: btnGlitchSliceA 6.5s infinite;
        }
        .glitch-subtle::after {
          color: var(--s-accent2, var(--magenta));
          animation: btnGlitchSliceB 6.5s infinite;
        }

        /* Card glitch effect for homepage projects & series */
        .card-glitch {
          position: relative;
          animation: cardGlitchPulse 8s infinite;
        }
        .card-glitch:hover .glitch-subtle::before {
          opacity: 0.8;
          transform: translate(-2px, 0);
          clip-path: inset(15% 0 45% 0);
        }
        .card-glitch:hover .glitch-subtle::after {
          opacity: 0.8;
          transform: translate(2px, 0);
          clip-path: inset(55% 0 10% 0);
        }
        @keyframes cardGlitchPulse {
          0%, 95%, 100% { box-shadow: none; }
          96% { box-shadow: -2px 0 0 var(--s-accent1, var(--cyan)), 2px 0 0 var(--s-accent2, var(--magenta)); }
          97% { box-shadow: 2px 0 0 var(--s-accent1, var(--cyan)), -1px 0 0 var(--s-accent2, var(--magenta)); }
        }

        @keyframes glitchA {
          0%, 94%, 100% { transform: translate(-2px, 0); clip-path: inset(0 0 0 0); }
          95% { transform: translate(-6px, 1px); clip-path: inset(20% 0 30% 0); }
          96% { transform: translate(-1px, -1px); clip-path: inset(60% 0 5% 0); }
        }
        @keyframes glitchB {
          0%, 94%, 100% { transform: translate(2px, 0); clip-path: inset(0 0 0 0); }
          95% { transform: translate(5px, -1px); clip-path: inset(10% 0 55% 0); }
          96% { transform: translate(1px, 1px); clip-path: inset(45% 0 15% 0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .glitch::before, .glitch::after,
          .btn-glitch, .btn-glitch::before, .btn-glitch::after,
          .glitch-subtle::before, .glitch-subtle::after,
          .card-glitch, .btn-glitch-inline {
            animation: none !important;
          }
        }

        /* -------- strip (home) -------- */
        .strip { padding: 24px 0 40px; border-top: 1px solid var(--line); }
        .strip-head { display: flex; align-items: baseline; justify-content: space-between; margin: 24px 0 16px; }
        .link-more { background: none; border: none; color: var(--cyan); font-family: 'Space Mono', monospace; font-size: 12px; }
        .strip-row { display: flex; gap: 16px; overflow-x: auto; padding-bottom: 8px; }
        .strip-row .card.is-compact { min-width: 260px; max-width: 300px; flex: 0 0 auto; }

        /* -------- section heads -------- */
        .section-head { padding: 64px 0 32px; max-width: 680px; }
        .section-title { font-family: 'Anton', sans-serif; font-size: clamp(30px, 5vw, 44px); margin: 4px 0 12px; }
        .section-sub { color: var(--muted); font-size: 15px; line-height: 1.6; }

        /* -------- series cards -------- */
        .grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 18px; }
        .card {
          text-align: left; background: var(--s-card-bg, var(--panel)); border: 1px solid var(--line); border-radius: 8px;
          padding: 0; color: inherit; overflow: hidden; transition: border-color .2s ease, transform .2s ease, box-shadow .2s ease;
          cursor: pointer;
        }
        .card.has-series-palette {
          border-color: color-mix(in srgb, var(--s-accent1) 32%, var(--line));
        }
        .card:hover {
          border-color: var(--s-accent1, var(--cyan));
          box-shadow: -2px 0 0 var(--s-accent1, var(--cyan)), 2px 0 0 var(--s-accent2, var(--magenta));
          transform: translateY(-2px);
        }
        .series-card-banner { position: relative; height: 150px; }
        .series-card-logo-overlay {
          position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
          background: rgba(10,10,12,0.35); padding: 16px;
        }
        .series-card-logo-overlay img { max-width: 80%; max-height: 90px; object-fit: contain; }
        .series-card-body { padding: 16px 18px 20px; }
        .series-card-title { font-family: 'Anton', sans-serif; font-size: 20px; margin: 0 0 6px; letter-spacing: 0.01em; }
        .series-card-meta-sm { font-family: 'Space Mono', monospace; font-size: 10.5px; color: var(--muted); margin: 0 0 6px; }
        .series-card-tagline { color: var(--muted); font-size: 13.5px; line-height: 1.5; margin: 0; }

        .back-link { background: none; border: none; color: var(--muted); font-family: 'Space Mono', monospace; font-size: 12px; margin: 24px 0 16px; padding: 0; }
        .back-link:hover { color: var(--ink); }

        /* -------- banner (detail) -------- */
        .banner {
          position: relative; min-height: 460px; border-radius: 10px; overflow: hidden;
          display: flex; align-items: flex-end;
          border: 1px solid var(--s-accent1, var(--line));
          box-shadow: -3px -3px 0px color-mix(in srgb, var(--s-accent1) 35%, transparent),
                      3px 3px 0px color-mix(in srgb, var(--s-accent2) 55%, transparent);
        }
        .banner-layer { position: absolute; inset: 0; transition: opacity 1.2s ease; }
        .banner-scrim { position: absolute; inset: 0; background: linear-gradient(0deg, rgba(10,10,12,0.96) 0%, rgba(10,10,12,0.6) 50%, rgba(10,10,12,0.2) 100%); }
        .banner-content { position: relative; padding: 40px; max-width: 740px; }
        .banner-badges { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; }
        .badge {
          display: inline-block; font-family: 'Space Mono', monospace; font-size: 10px; letter-spacing: 0.08em;
          color: var(--bg); background: var(--s-accent1, var(--cyan)); padding: 4px 8px; border-radius: 3px;
        }
        .banner-logo { font-family: 'Anton', sans-serif; font-size: clamp(30px, 5vw, 52px); line-height: 1; margin: 4px 0 10px; }
        .banner-custom-logo { margin: 6px 0 14px; }
        .banner-custom-logo img { max-width: 320px; max-height: 110px; object-fit: contain; display: block; }
        .banner-meta { font-family: 'Space Mono', monospace; font-size: 12px; color: var(--muted); margin: 0 0 14px; }
        .banner-synopsis { font-size: 15.5px; line-height: 1.65; max-width: 640px; color: var(--ink); opacity: 0.92; white-space: pre-line; }
        .banner-dots { position: absolute; right: 20px; bottom: 20px; display: flex; gap: 6px; }
        .dot-btn { width: 8px; height: 8px; border-radius: 50%; border: none; padding: 0; background: rgba(242,240,234,0.35); }
        .dot-btn.is-active { background: var(--s-accent1, var(--cyan)); transform: scale(1.2); }

        .gallery { padding: 40px 0 16px; }
        .gallery-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 12px; margin-top: 12px; }
        .gallery-tile { height: 140px; border-radius: 6px; border: 1px solid var(--line); background-color: var(--panel); transition: border-color .2s ease, transform .2s ease; }
        .gallery-tile:hover, .gallery-tile.is-selected { border-color: var(--s-accent1, var(--cyan)); transform: translateY(-2px); }

        /* -------- files section -------- */
        .files-section { padding: 32px 0 0; }
        .files-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 12px; margin-top: 12px; }
        .file-card {
          display: flex; align-items: center; justify-content: space-between; gap: 12px;
          padding: 14px 18px; border-radius: 6px; background: var(--panel); border: 1px solid var(--line);
          transition: border-color .2s ease;
        }
        .file-card:hover { border-color: var(--s-accent1, var(--cyan)); }
        .file-card-info { display: flex; flex-direction: column; gap: 4px; }
        .file-card-name { font-weight: 600; font-size: 14px; }
        .file-card-desc { font-size: 12px; color: var(--muted); }
        .file-card-action { font-family: 'Space Mono', monospace; font-size: 11px; color: var(--s-accent1, var(--cyan)); white-space: nowrap; }

        /* -------- projects -------- */
        .proj-list { display: flex; flex-direction: column; gap: 16px; }
        .proj-card {
          display: flex; gap: 20px; align-items: stretch;
          background: var(--panel); border: 1px solid var(--line); border-radius: 8px; padding: 20px 24px;
          transition: border-color .2s ease, transform .2s ease, box-shadow .2s ease;
        }
        .proj-card.is-clickable { cursor: pointer; }
        .proj-card.is-clickable:hover {
          border-color: var(--cyan);
          box-shadow: -2px 0 0 var(--cyan), 2px 0 0 var(--magenta);
          transform: translateY(-2px);
        }
        .proj-card-thumb { width: 180px; min-height: 120px; border-radius: 6px; flex-shrink: 0; border: 1px solid var(--line); }
        .proj-card-main { flex: 1; display: flex; flex-direction: column; justify-content: space-between; }
        .proj-card-top { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
        .proj-title { font-family: 'Anton', sans-serif; font-size: 22px; margin: 0; letter-spacing: 0.01em; }
        .status { font-family: 'Space Mono', monospace; font-size: 10px; letter-spacing: 0.08em; padding: 4px 9px; border-radius: 3px; text-transform: uppercase; }
        .status-dev { background: rgba(139,224,255,0.15); color: var(--cyan); }
        .status-proto { background: rgba(128,0,51,0.35); color: #ff80ab; }
        .proj-type { color: var(--muted); font-size: 13px; margin: 6px 0 8px; }
        .proj-desc { font-size: 14.5px; line-height: 1.6; margin: 0 0 14px; }
        .proj-card-footer { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; margin-top: auto; }
        .proj-card-actions { display: flex; align-items: center; gap: 14px; flex-wrap: wrap; }
        .proj-engine { font-family: 'Space Mono', monospace; font-size: 11px; color: var(--muted); margin: 0; }

        /* -------- merch -------- */
        .merch-group-section { padding: 16px 0 36px; border-top: 1px solid var(--line); }
        .merch-group-title { font-family: 'Anton', sans-serif; font-size: clamp(22px, 3.5vw, 30px); margin: 2px 0 4px; letter-spacing: 0.01em; }
        .series-merch-section { padding: 32px 0 8px; border-top: 1px solid var(--line); margin-top: 24px; }
        .merch-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 16px; }
        .merch-card { background: var(--s-card-bg, var(--panel)); border: 1px solid var(--line); border-radius: 8px; overflow: hidden; display: flex; flex-direction: column; transition: border-color .2s ease, transform .2s ease; }
        .merch-card:hover { border-color: var(--s-accent1, var(--cyan)); transform: translateY(-2px); }
        .merch-thumb { height: 160px; display: flex; align-items: center; justify-content: center; background: var(--panel2); }
        .merch-info { padding: 14px 16px 18px; flex: 1; display: flex; flex-direction: column; justify-content: space-between; }
        .merch-title { font-size: 15px; font-weight: 600; margin: 4px 0 12px; }
        .merch-row { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-top: auto; }
        .merch-price { font-family: 'Space Mono', monospace; font-size: 13px; color: var(--muted); }
        .merch-soon { font-family: 'Space Mono', monospace; font-size: 10px; letter-spacing: 0.06em; color: #ff80ab; text-transform: uppercase; }

        /* -------- admin dashboard -------- */
        .admin-login-box { max-width: 460px; margin: 72px auto; background: var(--panel); border: 1px solid var(--line); border-radius: 10px; padding: 32px; }
        .admin-login-form { display: flex; flex-direction: column; gap: 10px; }
        .admin-error { color: #ff80ab; font-family: 'Space Mono', monospace; font-size: 12px; margin: 4px 0 0; }
        .admin-header {
          display: flex; align-items: flex-end; justify-content: space-between; gap: 16px; flex-wrap: wrap;
          padding: 40px 0 24px; border-bottom: 1px solid var(--line);
        }
        .admin-header-actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .admin-save-notice { font-family: 'Space Mono', monospace; font-size: 11px; color: var(--cyan); }
        .admin-tabs { display: flex; gap: 6px; flex-wrap: wrap; padding: 18px 0; border-bottom: 1px solid var(--line); margin-bottom: 24px; }
        .admin-section { display: flex; flex-direction: column; gap: 20px; }
        .admin-section-bar { display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
        .admin-item-card { background: var(--panel); border: 1px solid var(--line); border-radius: 8px; padding: 22px 24px; display: flex; flex-direction: column; gap: 16px; }
        .admin-item-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; padding-bottom: 12px; border-bottom: 1px solid var(--line); }
        .admin-btn-group { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
        .admin-form-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 14px; }
        .admin-field { display: flex; flex-direction: column; gap: 6px; font-size: 12px; color: var(--muted); font-family: 'Space Mono', monospace; }
        .admin-color-row { display: flex; gap: 8px; align-items: center; }
        .admin-color-swatch { width: 42px; height: 38px; padding: 2px; border-radius: 4px; border: 1px solid var(--line); background: var(--bg); cursor: pointer; flex-shrink: 0; }
        .admin-checkbox { display: flex; align-items: center; gap: 10px; font-size: 13px; color: var(--ink); cursor: pointer; }
        .admin-input {
          background: var(--bg); color: var(--ink); border: 1px solid var(--line);
          border-radius: 4px; padding: 10px 12px; font-size: 13.5px; font-family: 'Inter', system-ui, sans-serif; width: 100%;
        }
        .admin-textarea { resize: vertical; line-height: 1.5; }
        .admin-select-sm { width: auto; min-width: 130px; }
        .admin-subbox { background: var(--panel2); border: 1px solid var(--line); border-radius: 6px; padding: 14px 16px; display: flex; flex-direction: column; gap: 10px; }
        .admin-subbox-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap; }
        .admin-inline-row { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
        .admin-inline-row > .admin-input { flex: 1; min-width: 160px; }
        .admin-banners-list { display: flex; flex-direction: column; gap: 8px; }
        .admin-banner-row { display: flex; gap: 10px; align-items: center; }
        .admin-banner-preview { width: 64px; height: 40px; border-radius: 4px; border: 1px solid var(--line); flex-shrink: 0; }
        .admin-hint { color: var(--muted); font-size: 12.5px; margin: 0; }
        .admin-merch-list { display: flex; flex-direction: column; gap: 16px; }

        /* -------- footer -------- */
        .footer { border-top: 1px solid var(--line); padding: 24px 28px; margin-top: 40px; }
        .footer-row { max-width: 1180px; margin: 0 auto; display: flex; justify-content: space-between; align-items: center; font-family: 'Space Mono', monospace; font-size: 11px; color: var(--muted); flex-wrap: wrap; gap: 8px; }

        @media (max-width: 860px) {
          .hero-split { grid-template-columns: 1fr; gap: 28px; }
          .hero-showcase { height: 260px; }
        }
        @media (max-width: 640px) {
          .nav { padding: 14px 16px; }
          .page { padding: 0 16px 60px; }
          .hero { padding: 48px 0 36px; }
          .banner-content { padding: 24px; }
          .nav-links { gap: 0; }
          .nav-link { padding: 8px 8px; font-size: 10px; }
          .proj-card { flex-direction: column; }
          .proj-card-thumb { width: 100%; height: 150px; }
        }
      `}</style>

      <Nav
        page={route.page}
        itemId={route.itemId}
        go={go}
        customPages={content.customPages}
        settings={content.settings}
      />
      {view}
      <Footer settings={content.settings} />
    </div>
  );
}
